import React, { createContext, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getValuesFromTransform } from '../utils/nodeHelpers';
import { getGroupTransforms } from '../utils/transformHelpers';
import { getRangeOfStaticTable, getRangeOfCellsUUID, sortGridData, getRepeatVariableDepth } from '../containers/ComplexDocumentDesign/complexDocumentHelper';
import { deepCopy, random4Characters } from '../utils/javascript';
import { OPTIONS_KEY, READ_ONLY_KEY, STATIC_TABLE_BLOCK, FILLABLE_RADIO_BUTTON_GROUP_TYPE, FILLABLE_CHECKBOX_TYPE, FILLABLE_RADIO_BUTTON_TYPE, FILLABLE_ELEMENT, CHECK_TYPE_KEY, IS_CHECKED_KEY } from '../utils/complexDocumentConstants';
import { getRandomFieldName } from '../utils/fillableHelper';
import { useDocumentContext } from './complexDocumentContext';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS } from './complexDocumentContext';
import { getGroupsFromMap } from '../containers/ComplexDocumentDesign/complexDocumentHelper';

export const PageElementsContext = createContext();
const DEFAULT_FONT_SIZE = 16;

const DEFAULT_CELL = {
  row: 1,
  column: 3,
  rowSpan: 1,
  columnSpan: 1,
  style: {
    border: {
      left: true,
      top: true,
      bottom: true,
      right: true,
    },
    padding: {
      top: 0,
      right: 0,
      left: 0,
      bottom: 0
    },
    backgroundColor: '#00000000',
    verticalAlignment: 'center'
  },
  contents: [
    {
      contentType: 'PARAGRAPH',
      style: {
        lineSpacing: 1.5,
        fontWeight: [],
        color: '#000000'
      },
      content: ''
    },
    {
      contentType: "LINE_BREAK",
      content: "\n",
      style: {
        lineSpacing: 1,
        alignment: "center",
        color: "#000000"
      }
    }
  ]
};

const PageElementsProvider = ({ isEmail, children, setPageUpdationDetail = undefined, parentElements = undefined, setParentElements = undefined, isComplexDesign = false }) => {
   
  const contextSelector = useCallback(store => store, []);
  const [documentContext, store] = useDocumentContext(contextSelector);
  
  const [pageElements, setPageElements] = useState([]);
  const [updatedElementStyles, setElementStyles] = useState();
  const [currentTemplate, setCurrentTemplate] = useState({});
  const [pagesUpdated, setUpdatePages] = useState([]);
  const [deletedPages, setDeletedPages] = useState([]);
  const [applyAll, setApplyAll] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isMovableClick, setMovableClick] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [sendForTesting, setSendForTesting] = useState({
    sendToTest: true,
    testVersion: true,
  });
  const [ isTableEditing, setTableEditing ] = useState(false);
  const [ isTableFormatEnabled, setTableFormat ] = useState(false);
  const [focusedCheckBox, setCheckBoxFocused] = useState('');
  const [generatingText, setGeneratingText] = useState(false);
  const [groupedElementIds, setGroupedElementIds] = useState([]);

  const storedElements = parentElements || pageElements;
  const setElementsStore = setParentElements || setPageElements;

  const updateElementStyles = useCallback(elementStyles => {
    let newElementStyles = JSON.parse(JSON.stringify(elementStyles)); // Deep copying an Object
    setElementStyles(newElementStyles);
  }, []);
  
  useEffect(() => {
    if (setPageUpdationDetail) {
      setPageUpdationDetail(pagesUpdated);
    }
    // eslint-disable-next-line
  }, [pagesUpdated]);

  const handleStoredElements = useCallback((elements, isDropped = false) => {
    if (storedElements.length > 0 || isDropped) {
      handlePageHistory(elements);
    }
    setElementsStore(storedElements => {
      let newElements = [...storedElements];
      if (isDropped) {
          elements.forEach((newEl, index) => {
            if (isComplexDesign) {
              // Do nothing
              // TODO : Check if validation is needed only within the block or across all blocks
            } else if (newEl.elementDetails.type === 'dynamic-table') {
              const existingElements = newElements.filter(el => el.elementDetails.type === 'dynamic-table');
              newEl.elementDetails.value.variableName = `table-${existingElements.length + 1}`;
              newEl.elementDetails.value.tableHeader = newEl.elementDetails.value.tableHeader.map(header => Object.assign({}, header, { keyToMap: '' }));
            } else if (newEl.elementDetails.type === 'checkboxGroup') {
              const existingElements = newElements.filter(el => el.elementDetails.type === newEl.elementDetails.type);
              newEl.elementDetails.value.formField.fieldName = `checkGroup-${existingElements.length + 1}`;
            } else if (newEl.elementDetails.type === 'radiobuttongroup') {
              const existingElements = newElements.filter(el => el.elementDetails.type === newEl.elementDetails.type);
              newEl.elementDetails.value.formField.fieldName = `radioGroup-${existingElements.length + 1}`;
            } else if (newEl.elementDetails.uiType === 'textBox') {
              const existingElements = newElements.filter(el => el.elementDetails.uiType === newEl.elementDetails.uiType);
              newEl.elementDetails.validations.fieldName = `textBox-${existingElements.length + 1}`;
            } else if (newEl.elementDetails.uiType === 'signature') {
              const existingElements = newElements.filter(el => el.elementDetails.uiType === newEl.elementDetails.uiType);
              newEl.elementDetails.value.formField.fieldName = `signature-${existingElements.length + 1}`;
            } else if (newEl.elementDetails.uiType === 'combobox') {
              const existingElements = newElements.filter(el => el.elementDetails.uiType === newEl.elementDetails.uiType);
              newEl.elementDetails.validations.fieldName = `combobox-${existingElements.length + 1}`;
            }
            newElements = [...newElements, newEl];
          });
      } else {
        newElements = [...storedElements, ...elements];
      }
      return newElements;
    });
  }, [handlePageHistory, isComplexDesign]);

  const swapElements = useCallback((type, elementId) => {
    setElementsStore((elements) => {
      const elementIndex = elements.findIndex(element => element.uuid === elementId);
      let newElementsList = elements
      switch (type) {
        case 'sendBackward':
          if (elementIndex > 0) {
            newElementsList = JSON.parse(JSON.stringify(elements)); // Deep copying an Object
            let temp = newElementsList[elementIndex - 1];
            newElementsList[elementIndex - 1] = newElementsList[elementIndex];
            newElementsList[elementIndex] = temp;
          }
          break;
        case 'sendToBack':
          if (elementIndex > 0) {
            newElementsList = JSON.parse(JSON.stringify(elements)); // Deep copying an Object
            let temp = newElementsList[0];
            newElementsList[0] = newElementsList[elementIndex];
            newElementsList.forEach((elementItem, index) => {
              if (index > 0 && index < elementIndex + 1) {
                let tempElement = elementItem;
                newElementsList[index] = temp;
                temp = tempElement;
              }
            });
          }
          break;
        case 'bringForward':
          if (elementIndex < newElementsList.length - 1) {
            newElementsList = JSON.parse(JSON.stringify(elements)); // Deep copying an Object
            let temp = newElementsList[elementIndex];
            newElementsList[elementIndex] = newElementsList[elementIndex + 1];
            newElementsList[elementIndex + 1] = temp;
          }
          break;
        case 'bringToFront':
          if (elementIndex < newElementsList.length - 1) {
            newElementsList = JSON.parse(JSON.stringify(elements)); // Deep copying an Object
            let temp = newElementsList[newElementsList.length - 1];
            newElementsList[newElementsList.length - 1] = newElementsList[elementIndex];
            newElementsList.forEach((elementItem, index) => {
              if (index > elementIndex && index < newElementsList.length - 1) {
                newElementsList[index - 1] = elementItem;
              } else if (index === newElementsList.length - 1) {
                newElementsList[index - 1] = temp;
              }
            });
          }
          break;
        default:
          break;
      }
      return newElementsList;
    });
  }, []);

  const groupingElements = useCallback((type, groupedElementIds = [], groupName) => {
    setElementsStore(elements => {
      let existingGroupId;
      const groupId = uuidv4();
      let groupsId;
      groupedElementIds.forEach(element => {
        const elementIndex = elements.findIndex(e => {
          return e.uuid === element;
        });
        if (!existingGroupId) {
          existingGroupId = elements[elementIndex].groupId;
        }
        groupsId = type === 'Group' ? existingGroupId ? existingGroupId : groupId : '';
        elements[elementIndex].groupId = groupsId;
      });
      const existingGroupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
      const groupDetail = existingGroupMap.get(groupsId); 
      const isBusinessLogicPresent = groupDetail && groupDetail.isBLPresent?  groupDetail.isBLPresent : false;
      if (type === 'Group') {
        existingGroupMap.set(groupsId, { groupName: groupName, isBLPresent: isBusinessLogicPresent })
      }
      if (type === 'UnGroup') {
        existingGroupMap.set(existingGroupId, { groupName: groupName, isBLPresent: false })
      }
      store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: existingGroupMap });      
      return elements;
    }, true);
  }, []);

  const deleteElementDetails = useCallback((deleteList, targets, isComplexDesign = false) => {
    setElementsStore(storedElements => {
      const newStoredElements = [];
      storedElements.forEach(storedElement => {
        if (deleteList.includes(storedElement.uuid)) {
          const el = isComplexDesign ? storedElement : storedElement.elementDetails;
          const subElementsToBeDeleted = deleteList.map((uuid, index) => ({ uuid, index })).filter(obj => obj.uuid === el.uuid);
          // update the element if multiple sub elements are present
          if (el?.fillableType === FILLABLE_RADIO_BUTTON_GROUP_TYPE && el?.contents?.length > 1) {
            // Checking if all the sub elements were removed in the process
            if (subElementsToBeDeleted.length < el.contents.length) {
              const removeList = subElementsToBeDeleted.map(details => {
                const deleteIndex = details.index; // Get the target index
                return parseInt(targets[deleteIndex].getAttribute('data-element-index')); // get the element Index from attribute
              });
              let newEl;
              if (isComplexDesign) {
                newEl = {
                  ...el,
                  contents: el.contents.filter((subEl, subIndex) => !removeList.includes(subIndex))
                }
              } else{
                newEl = {
                  ...el,
                  elementDetails: {
                    ...el.elementDetails,
                    contents: el.elementDetails.contents.filter((subEl, subIndex) => !removeList.includes(subIndex))
                  }
                }
              }
              newStoredElements.push(newEl);
            }
          }
          // Otherwise do not include it in new list
        } else {
          newStoredElements.push(storedElement);
        }
      });
      return newStoredElements;
    });
  }, []);

  const pasteElements = (copiedElements, callback = () => {}, rect = undefined, domElementId = undefined, source = undefined) => {
    let transformCopiedElements = [...copiedElements];
    console.log({ rect, domElementId });
    if (rect && domElementId !== undefined && domElementId !== source) {
      const domElement = document.getElementById(domElementId);
      if (domElementId) {
        const minTop = copiedElements.reduce((acc, el) => {
          return Math.min(el.style.y, acc);
        }, copiedElements[0]?.style?.y);
        if (minTop > (domElement.offsetHeight * 2 / 3) && (minTop + rect.height) > domElement.offsetHeight) {
          // Paste elements will go beyond the height of the rect
          transformCopiedElements = transformCopiedElements.map(el => {
            el.style.y -= minTop;
            return el;
          });
        }
      }
    } else if (domElementId === source) {
      transformCopiedElements = transformCopiedElements.map(el => {
        el.style.x += 50;
        el.style.y += 50;
        return el;
      });
    }
    setElementsStore(elements => {
      callback();
      return [...elements, ...transformCopiedElements];
    });
  };

  const copyElements = (elementIds, isComplexDesign = false) => {
    const STYLE_KEY = isComplexDesign ? 'style' : 'styles';
    let elementsToCopy;
    if (isComplexDesign) {
      elementsToCopy = storedElements.filter(element => {
        return elementIds.includes(element.uuid) && element.fillableType !== FILLABLE_RADIO_BUTTON_GROUP_TYPE;
      });
    } else {
      elementsToCopy = storedElements.filter(element => {
        return elementIds.includes(element.uuid) && element.elementDetails.type !== 'table';
      });
    }
    if (elementsToCopy.length === 0) return [];
    // let orderedElements = mapOrder(elementsToCopy, elementIds, 'uuid');
    let orderedElements = [...elementsToCopy];
    if (orderedElements && orderedElements.length > 0) {
      const groupIdsArray =[];
      const groupMap = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS];
      const newElementsList = deepCopy(orderedElements).map(elementItem => {
        elementItem.uuid = uuidv4();
        // creating new group id while copy
        const existingGroupId = elementItem?.groupId;
        const newGroupId = uuidv4();
        if (existingGroupId && !groupIdsArray.some(item => item.existingGroupId === existingGroupId)) {
          groupIdsArray.push({ existingGroupId: existingGroupId, newGroupId: newGroupId })
        }
        const newGroupIdfromArray = groupIdsArray.find(i => i.existingGroupId === existingGroupId)?.newGroupId ?? null;
        if (existingGroupId) {
          const group_id = newGroupIdfromArray ? newGroupIdfromArray : newGroupId;
          groupMap.set(group_id, { groupName: '', isBLPresent: false });
          elementItem.groupId = group_id;
        }
        if (
          !isComplexDesign &&
          elementItem?.elementDetails &&
          elementItem?.elementDetails[STYLE_KEY] &&
          elementItem.elementDetails[STYLE_KEY].transform
        ) {
          const transformStyles = elementItem.elementDetails[STYLE_KEY].transform.split(' ');
          let result = {};
          transformStyles.forEach(data => {
            let key = data.split('(')[0];
            let value = data && data.match(/\((.*)\)/)[1];
            value = Number(value.replace(/[^\d.-]/g, ''));
            let pair = { [key]: value };
            result = { ...result, ...pair };
          });
          if (
            result &&
            result.translateX.toString() &&
            result.translateY.toString() &&
            result.rotate.toString()
          ) {
            elementItem.elementDetails[STYLE_KEY].transform = `translateX(${result.translateX +
              50}px) translateY(${result.translateY + 50}px) rotate(${
              result.rotate
            }deg) scaleX(1) scaleY(1)`;
          }
        } else if (elementItem && isComplexDesign) {
          /*elementItem[STYLE_KEY].x = elementItem[STYLE_KEY].x + 50;
          elementItem[STYLE_KEY].y = elementItem[STYLE_KEY].y + 50;*/
          if (elementItem.contents?.length > 0) {
            elementItem.contents = elementItem.contents.map(content =>
              Object.assign({}, content, { uuid: uuidv4() }),
            );
          }
        }
        if (elementItem?.contentType === FILLABLE_ELEMENT) {
          if (elementItem?.fillableType === FILLABLE_CHECKBOX_TYPE) {
            elementItem.formOptions.fieldName = `check-box-${random4Characters()}`;
          } else if (elementItem?.fillableType === FILLABLE_RADIO_BUTTON_TYPE) {
            elementItem.formOptions.fieldName = `radio-button-${random4Characters()}`;
          }
        }
        return elementItem;
      });
      const groupArray = getGroupsFromMap(newElementsList, groupMap);
      newElementsList.groups = groupArray;
      return [...newElementsList];
    }
    return [];
  };

  const copyGridElement = ({ start, end }) => {
    return getRangeOfCellsUUID(start, end, storedElements[0].contents);
  };

  const processPasteAction = (parentUUID, cell, pasteCell, copiedCellData, itemUUID, methods) => {
    let userAction = false;
    let details = {
      flowUUID: parentUUID,
      elementUUID: cell.uuid,
      alreadyHasData: !!pasteCell,
      type: 'update',
      op: 'update',
    };
    let pastingInEmptyCell = false;

    if (copiedCellData) {
      if (!pasteCell) {
        pastingInEmptyCell = true;
        pasteCell = { ...copiedCellData, uuid: cell.uuid };
      }
      if (copiedCellData.contentType === FILLABLE_ELEMENT) {
        copiedCellData.formOptions.fieldName = getRandomFieldName(copiedCellData.fillableType);
      }

      details.isTextElement = copiedCellData.contentType === 'text';
      details.droppedElement = {
        ...pasteCell,
        ...copiedCellData,
        uuid: cell.uuid,
        contentType: copiedCellData.contentType,
        contents: copiedCellData.contentType === 'text' ? copiedCellData.contents : [],
        style: copiedCellData.style,
      };

      userAction =
        pastingInEmptyCell ||
        !(pasteCell.contentType === 'text' && copiedCellData.contentType === 'text');

      methods.updateBlock('static-table-cells', itemUUID, details, userAction);
    }
  };

  const pasteIntoSelectedCells = (selectedCells, copiedCells, methods, parentUUID, itemUUID) => {
    const data = storedElements ? sortGridData(deepCopy(storedElements)[0].contents) : [];
    const getParentDetailsOfCell = cellUUID => data.find(cell => cell.uuid === cellUUID);

    const selectedRows =
      Math.max(...selectedCells.map(cell => getParentDetailsOfCell(cell).row)) -
      Math.min(...selectedCells.map(cell => getParentDetailsOfCell(cell).row)) +
      1;
    const selectedColumns =
      Math.max(...selectedCells.map(cell => getParentDetailsOfCell(cell).column)) -
      Math.min(...selectedCells.map(cell => getParentDetailsOfCell(cell).column)) +
      1;

    const copiedRows =
      Math.max(...copiedCells.map(cell => getParentDetailsOfCell(cell).row)) -
      Math.min(...copiedCells.map(cell => getParentDetailsOfCell(cell).row)) +
      1;
    const copiedColumns =
      Math.max(...copiedCells.map(cell => getParentDetailsOfCell(cell).column)) -
      Math.min(...copiedCells.map(cell => getParentDetailsOfCell(cell).column)) +
      1;

    // Create a map for easy lookup of copied cell content
    const copiedElements = copiedCells.map(copiedCellsUUID =>
      getParentDetailsOfCell(copiedCellsUUID),
    );
    const copiedContentMap = {};
    copiedElements.forEach(cell => {
      const relativeRow = cell.row - Math.min(...copiedElements.map(c => c.row));
      const relativeColumn = cell.column - Math.min(...copiedElements.map(c => c.column));
      copiedContentMap[`${relativeRow},${relativeColumn}`] = cell;
    });

    // Update the contents of the target cells with replication
    data.forEach(cell => {
      const selectedCellIndex = selectedCells.findIndex(uuid => uuid === cell.uuid);
      if (selectedCellIndex !== -1) {
        const relativeRow =
          cell.row - Math.min(...selectedCells.map(c => getParentDetailsOfCell(c).row));
        const relativeColumn =
          cell.column - Math.min(...selectedCells.map(c => getParentDetailsOfCell(c).column));

        const replicatedRow = relativeRow % copiedRows;
        const replicatedColumn = relativeColumn % copiedColumns;

        if (copiedContentMap[`${replicatedRow},${replicatedColumn}`] !== undefined) {
          let pasteCell =
            methods.get()[cell.uuid] !== undefined ? deepCopy(methods.get()[cell.uuid]) : undefined;
          let copiedCellData = deepCopy(methods.get()[
            copiedContentMap[`${replicatedRow},${replicatedColumn}`].uuid
          ]);
          processPasteAction(parentUUID, cell, pasteCell, copiedCellData, itemUUID, methods);
        }
      }
    });
  };

  const pasteCellsWithExpansion = (pasteCellUUID, copiedCells, methods, parentUUID, itemUUID) => {
    const data = storedElements ? sortGridData(deepCopy(storedElements)[0].contents) : [];
    const getParentDetailsOfCell = cellUUID => data.find(cell => cell.uuid === cellUUID);

    // Find the paste starting cell
    const pasteStartCell = getParentDetailsOfCell(pasteCellUUID);

    if (!pasteStartCell) {
      return data; // If paste start cell is not found, return original data
    }

    // Determine the size of the copied data
    const copiedRows =
      Math.max(...copiedCells.map(cell => getParentDetailsOfCell(cell).row)) -
      Math.min(...copiedCells.map(cell => getParentDetailsOfCell(cell).row)) +
      1;
    const copiedColumns =
      Math.max(...copiedCells.map(cell => getParentDetailsOfCell(cell).column)) -
      Math.min(...copiedCells.map(cell => getParentDetailsOfCell(cell).column)) +
      1;

    // Starting positions for paste
    const startRow = pasteStartCell.row;
    const startColumn = pasteStartCell.column;

    // Maximum existing rows and columns in the table
    const maxRow = Math.max(...data.map(cell => cell.row));
    const maxColumn = Math.max(...data.map(cell => cell.column));

    let hasParentlevelChanges = false;
    // Expand the table uniformly, ensuring all rows have the same number of columns
    // for (let row = 1; row <= Math.max(maxRow, startRow + copiedRows - 1); row++) {
    //   for (let col = 1; col <= Math.max(maxColumn, startColumn + copiedColumns - 1); col++) {
    //     if (!data.some(cell => cell.row === row && cell.column === col)) {
    //       // Add new cells if they don't exist
    //       hasParentlevelChanges =true;
    //       data.push({
    //         row,
    //         column: col,
    //         style: {
    //           "verticalAlignment": "middle",
    //           "border": {
    //               "thickness": 1,
    //               "borderStyle": "SOLID",
    //               "borderColor": "#000000",
    //               "left": true,
    //               "top": true,
    //               "bottom": true,
    //               "right": true
    //           },
    //           "padding": {
    //               "top": 0,
    //               "right": 0,
    //               "left": 0,
    //               "bottom": 0
    //           },
    //           "backgroundColor": "#00000000"
    //         },
    //         contents: [],
    //         uuid: uuidv4() // Generate new UUIDs for new cells
    //       });
    //     }

    //     // if (!existingCells.has(`${row},${col}`)) {
    //     //   const existingCell = data.find(cell => {
    //     //     return (
    //     //       row >= cell.row &&
    //     //       row < cell.row + cell.rowSpan &&
    //     //       col >= cell.column &&
    //     //       col < cell.column + cell.columnSpan
    //     //     );
    //     //   });
    //     //   console.log('existingCell==>', existingCell);
    //     // }

    //   }
    // }

    // Create a map for easy lookup of copied cell content
    const copiedElements = copiedCells.map(copiedCellsUUID =>
      getParentDetailsOfCell(copiedCellsUUID),
    );
    let copiedContentMap = {};
    copiedElements.forEach(cell => {
      const relativeRow = cell.row - Math.min(...copiedElements.map(c => c.row));
      const relativeColumn = cell.column - Math.min(...copiedElements.map(c => c.column));
      copiedContentMap[`${relativeRow},${relativeColumn}`] = cell;
    });

    // Update the contents of the target cells
    data.forEach(cell => {
      const relativeRow = cell.row - startRow;
      const relativeColumn = cell.column - startColumn;

      if (
        relativeRow >= 0 &&
        relativeRow < copiedRows &&
        relativeColumn >= 0 &&
        relativeColumn < copiedColumns &&
        copiedContentMap[`${relativeRow},${relativeColumn}`] !== undefined
      ) {
        // Update the contents if within bounds
        let pasteCell =
          methods.get()[cell.uuid] !== undefined ? deepCopy(methods.get()[cell.uuid]) : undefined;
        let copiedCellData = deepCopy(methods.get()[
          copiedContentMap[`${relativeRow},${relativeColumn}`].uuid
        ]);
        processPasteAction(parentUUID, cell, pasteCell, copiedCellData, itemUUID, methods);
      }
    });
    return hasParentlevelChanges ? data : undefined;
  };

  const pasteGridElements = (
    selectedRange,
    copiedCellsUUID,
    methods,
    itemUUID,
    parentUUID,
    callback = () => {},
  ) => {
    let updatedData;
    if (selectedRange?.start && selectedRange?.end) {
      const selectedCellsUUID = getRangeOfCellsUUID(
        selectedRange.start,
        selectedRange.end,
        deepCopy(storedElements)[0].contents,
      );
      updatedData = pasteIntoSelectedCells(
        selectedCellsUUID,
        copiedCellsUUID,
        methods,
        parentUUID,
        itemUUID,
      );
    } else if (selectedRange?.start && !selectedRange?.end) {
      updatedData = pasteCellsWithExpansion(
        selectedRange.start,
        copiedCellsUUID,
        methods,
        parentUUID,
        itemUUID,
      );
    }

    if (updatedData) {
      setElementsStore(elements => {
        callback();
        return [{ ...storedElements[0], contents: updatedData }];
      });
    } else {
      callback();
    }
  };

  const deleteCellContnet = ({ start, end }, itemUUID, parentUUID, methods) => {
    const selectedCells = getRangeOfCellsUUID(start, end, storedElements[0].contents);
    selectedCells.forEach(cellUUID => {
      const cellContent = methods.get()[cellUUID];
      cellContent && methods.updateBlock(
        'static-table-cells',
        itemUUID,
        {
          droppedElement: { ...cellContent },
          flowUUID: parentUUID,
          elementUUID: cellUUID,
          type: 'delete',
        },
        true,
      );
    });
  };

  const updateComplexElementDetails = useCallback(
    (uuid, details, updationType) => {
      setElementsStore(storedElements => {
        console.log('storedElements',storedElements)
        const elementIndex = storedElements.findIndex(element => element.uuid === uuid);
        let newStoredElements = JSON.parse(JSON.stringify(storedElements)); // Deep copying an Object
        let hasChanged = false;
        let textChanges = false;
        let isStyleChanged = false;
        let isTableStyleChanged = false;
        let key;
        if (elementIndex !== -1) {
          switch (updationType) {
            case 'style':
              hasChanged = true;
              if (details.width && typeof details.width === 'string') {
                details.width = parseInt(`${details.width}`.replaceAll('px', ''));
              }
              if (details.height && typeof details.height === 'string') {
                details.height = parseInt(`${details.height}`.replaceAll('px', ''));
              }
              newStoredElements[elementIndex].style = {
                ...newStoredElements[elementIndex].style,
                ...details,
              };
              isStyleChanged = true;
              break;
            case 'group-style':
              hasChanged = true;
              if (details.style.width && typeof details.style.width === 'string') {
                details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
              }
              if (details.style.height && typeof details.style.height === 'string') {
                details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
              }
              newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map((el, index) => {
                if (index === details.elementIndex) {
                  return {
                    ...el,
                    style: {
                      ...el.style,
                      ...details.style
                    }
                  }
                }
                return el;
              });
              isStyleChanged = true;
              break;
            case 'barcode-value':
              hasChanged = true;
              if (details.style.width && typeof details.style.width === 'string') {
                details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
              }
              if (details.style.height && typeof details.style.height === 'string') {
                details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
              }
              newStoredElements[elementIndex].style = {
                ...newStoredElements[elementIndex].style,
                ...details.style,
              };
              isStyleChanged = true;
              newStoredElements[elementIndex].content = details.content;
              break;
            case 'svg-value':
              hasChanged = true;
              newStoredElements[elementIndex].url = details;
              break;
            case 'svg-value-with-styles':
              hasChanged = true;
              const updatedStyles = details.style;
              if (updatedStyles.width && typeof updatedStyles.width === 'string') {
                updatedStyles.width = parseInt(`${updatedStyles.width}`.replaceAll('px', ''));
              }
              if (updatedStyles.height && typeof updatedStyles.height === 'string') {
                updatedStyles.height = parseInt(`${updatedStyles.height}`.replaceAll('px', ''));
              }
              if(details.staticTableBlock){
                delete updatedStyles.width;
              }
              newStoredElements[elementIndex].url = details.url;
              newStoredElements[elementIndex].style = {
                ...newStoredElements[elementIndex].style,
                ...updatedStyles,
              };
              isStyleChanged = true;
              break;
            case 'svg-colors':
              hasChanged = true;
              newStoredElements[elementIndex].svgColors = details;
              break;
            case 'form-options':
              hasChanged = true;
              newStoredElements[elementIndex][OPTIONS_KEY] = {
                ...newStoredElements[elementIndex][OPTIONS_KEY],
                ...details.options
              };
              if (details.style) {
                if (details.style.width && typeof details.style.width === 'string') {
                  details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
                  updateElementStyles({ width: `${details.style.width}px`, uuid: uuid });
                }
                if (details.style.height && typeof details.style.height === 'string') {
                  details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
                  updateElementStyles({ height: `${details.style.height}px`, uuid: uuid });
                }
                newStoredElements[elementIndex].style = {
                  ...newStoredElements[elementIndex].style,
                  ...details.style
                };
                isStyleChanged = true;
              }
              if (details?.extraFormElementsUuids?.length) {
                details.extraFormElementsUuids.forEach(elUuid => {
                  const elIndex = storedElements.findIndex(element => element.uuid === elUuid);
                  if (elIndex > -1 && newStoredElements?.[elIndex]) {
                    if (details?.style) {
                      newStoredElements[elIndex].style = {
                        ...newStoredElements[elIndex].style,
                        ...details.style,
                      };
                    }
                    if (
                      details.options &&
                      (CHECK_TYPE_KEY in details.options || READ_ONLY_KEY in details.options)
                    ) {
                      newStoredElements[elIndex].formOptions = {
                        ...newStoredElements[elIndex].formOptions,
                        ...details.options,
                      };
                    }
                    if (details.negativeOptions && IS_CHECKED_KEY in details.negativeOptions) {
                      newStoredElements[elIndex].formOptions = {
                        ...newStoredElements[elIndex].formOptions,
                        ...details.negativeOptions,
                      };
                    }
                  }
                });
              }
              if (details.contents) {
                newStoredElements[elementIndex].contents = details.contents;
              }
              break;
            case 'text-scaling':
              if (details.refElement) {
                details.refElement.dispatchEvent(new CustomEvent('changeScaling', { detail: { scaling: details.scaling, style: details.style } }))
              }
              break;
            case 'table-width-scaling':
              if (details.refElement) {
                hasChanged = true;
                const oldWidth = newStoredElements[elementIndex].style.width;
                newStoredElements[elementIndex].style = Object.assign({}, newStoredElements[elementIndex].style, {
                  width: details.style.width,
                  height: details.style.height,
                  tableProperties: Object.assign({}, newStoredElements[elementIndex].style.tableProperties, {
                    rowHeights: Array.from(details.refElement.querySelectorAll('tr')).map(row => row.clientHeight),
                    columnWidths: Array.from(details.refElement.querySelectorAll('col')).map(col => Number(col.style.width.replace(/px$/, ''))),
                  })
                });
                const sumOfColWidths = newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce(
                  (acc, colWidth) => acc + colWidth,
                  0
                );
                if (sumOfColWidths < details.style.width) {
                  const diff = details.style.width - sumOfColWidths;
                  const colDiff = diff / newStoredElements[elementIndex].style.tableProperties.columnWidths.length;
                  console.log({ diff, colDiff, sumOfColWidths, newWidth: details.style.width });
                  newStoredElements[elementIndex].style.tableProperties = Object.assign({}, newStoredElements[elementIndex].style.tableProperties, {
                    columnWidths: newStoredElements[elementIndex].style.tableProperties.columnWidths.map(colWidth => colWidth + colDiff)
                  })
                }
                details.refElement.dispatchEvent(new CustomEvent('tableScalingEnd', { detail: { style: details.style, newContents: newStoredElements[elementIndex].contents } }))
                isStyleChanged = true;
                // isTableStyleChanged = true;
              }
              break;
            case 'table-scaling':
              if (details.refElement) {
                hasChanged = true;
                newStoredElements[elementIndex].style = Object.assign({}, newStoredElements[elementIndex].style, {
                  width: details.style.width,
                  height: details.style.height,
                  tableProperties: Object.assign({}, newStoredElements[elementIndex].style.tableProperties, {
                    rowHeights: Array.from(details.refElement.querySelectorAll('tr')).map(row => row.clientHeight),
                    columnWidths: Array.from(details.refElement.querySelectorAll('col')).map(col => Number(col.style.width.replace(/px$/, ''))),
                  })
                });
                newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                  return Object.assign({}, cell, {
                    style: Object.assign({}, cell.style, {
                      padding: {
                        top: cell.style.padding.top * details.scaling,
                        bottom: cell.style.padding.bottom * details.scaling,
                        left: cell.style.padding.left * details.scaling,
                        right: cell.style.padding.right * details.scaling,
                      }
                    }),
                    contents: cell.contents.map(textContent => {
                      return Object.assign({}, textContent, {
                        style: Object.assign({}, textContent.style, {
                          fontSize: (textContent.style.fontSize || DEFAULT_FONT_SIZE) * details.scaling
                        })
                      })
                    })
                  });
                });
                isTableStyleChanged = true;
                isStyleChanged = true;
                console.log("Changes in scaling :: ", newStoredElements[elementIndex]);
                details.refElement.dispatchEvent(new CustomEvent('tableScalingEnd', { detail: { scaling: details.scaling, style: details.style, newContents: newStoredElements[elementIndex].contents } }))
              }
              break;
            case 'text-value-with-style':
              hasChanged = true;
              textChanges = true;
              newStoredElements[elementIndex].contents = details.contents;
              if (details.style.width && typeof details.style.width === 'string') {
                details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
              }
              if (details.style.height && typeof details.style.height === 'string') {
                details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
              }
              newStoredElements[elementIndex].style = {
                ...newStoredElements[elementIndex].style,
                ...details.style // TODO : Capture change in height
              }
              isStyleChanged = true;
              if (details.style) {
                const elementStyles = { uuid };
                if (details.style.height) {
                  elementStyles.height = `${details.style.height}px`;
                }
                if (details.style.width) {
                  elementStyles.width = `${details.style.width}px`;
                }
                updateElementStyles(elementStyles);
              }
              break;
            case 'table-header-value-with-style':
              hasChanged = true;
              textChanges = true;
              key = 'headerContent';
              if (details.isGroupHeader) {
                key = 'groupHeaderContent';
              }
              if (details.isRow) {
                key = 'content';
              }

              newStoredElements[elementIndex][key].contents = details.contents;
              if (details.style.width && typeof details.style.width === 'string') {
                details.style.width = parseInt(`${details.style.width}`.replaceAll('px', ''));
              }
              if (details.style.height && typeof details.style.height === 'string') {
                details.style.height = parseInt(`${details.style.height}`.replaceAll('px', ''));
              }
              newStoredElements[elementIndex][key].style = {
                ...newStoredElements[elementIndex][key].style,
                ...details.style // TODO : Capture change in height
              }
              console.log({ newStoredElements });
              isStyleChanged = true;
              if (key === 'content') {
                const deepestRelation = getRepeatVariableDepth(
                  details.contents,
                  details.relationships,
                );
                newStoredElements[elementIndex].repeatVariable = deepestRelation?.repeatVariable ??
                    newStoredElements[elementIndex]?.repeatVariable ??
                    null;
              }
              
              break;
            case 'contents':
              hasChanged = true;
              newStoredElements[elementIndex].contents = details;
              break;
            case 'content':
              hasChanged = true;
              newStoredElements[elementIndex].content = details;
              break;
            case 'combobox-styles':
              hasChanged = true;
              if (details.width && typeof details.width === 'string') {
                details.width = parseInt(`${details.width}`.replaceAll('px', ''));
              }
              if (details.height && typeof details.height === 'string') {
                details.height = parseInt(`${details.height}`.replaceAll('px', ''));
                updateElementStyles({ height: `${details.height}px`, uuid: uuid });
              }
              newStoredElements[elementIndex].style = {
                ...newStoredElements[elementIndex].style,
                ...details,
              };
              isStyleChanged = true;
              break;
              case 'contents-with-style':
                hasChanged = true;
                let {contents, style} = details;
                newStoredElements[elementIndex].contents = contents;
                if (style.width && typeof style.width === 'string') {
                  style.width = parseInt(`${style.width}`.replaceAll('px', ''));
                }
                if (style.height && typeof style.height === 'string') {
                  style.height = parseInt(`${style.height}`.replaceAll('px', ''));
                }
                newStoredElements[elementIndex].style = {
                  ...newStoredElements[elementIndex].style,
                  ...style,
                };
                isStyleChanged = true;
                updateElementStyles({ width: `${newStoredElements[elementIndex].style.width}px`, height: `${newStoredElements[elementIndex].style.height}px`, uuid: uuid });
           
                break;

              case 'table-style-alone':
                hasChanged = true;

                if (details.width && typeof details.width === 'string') {
                  details.width = parseInt(`${details.width}`.replaceAll('px', ''));
                }
                if (details.height && typeof details.height === 'string') {
                  details.height = parseInt(`${details.height}`.replaceAll('px', ''));
                }
                newStoredElements[elementIndex].style = {
                  ...newStoredElements[elementIndex].style,
                  ...details,
                };

                console.log('newStoredElements[elementIndex].style',newStoredElements[elementIndex].style)
                isStyleChanged = true;
                // updateElementStyles({ width: `${details.width}px`, height: `${details.height}px`, uuid: uuid });
           
                break;
              case 'image':
                hasChanged = true;

                if (details.style) {
                  newStoredElements[elementIndex].style = {
                    ...newStoredElements[elementIndex].style,
                    ...details.style,
                  };
                }
                newStoredElements[elementIndex].content = details.content;
                break;
              case 'static-table-add-column':
                hasChanged = true;

                if (details.index !== undefined) {
                  let columnIndex = details.index + (details.isLeft ? 0 : 1);

                  let newCells = [];
                  if (details.isLeft) {
                    const numberOfRows = newStoredElements[elementIndex].style.tableProperties.rowHeights.length;
                    newCells = (new Array(numberOfRows)).fill(0).map((e, rowIndex) => {
                      return Object.assign({}, DEFAULT_CELL, {
                        uuid: uuidv4(),
                        column: columnIndex + 1, // Column index starts with 1 not 0
                        row: rowIndex + 1, // TODO : Carry over the styles from the existing table??
                      })
                    });
                  } else {
                    newCells = newStoredElements[elementIndex].contents
                      .filter(cell => cell.column === columnIndex && cell.columnSpan === 1)
                      .map(cell => {
                        return Object.assign({}, cell, {
                          uuid: uuidv4(),
                          contents: [...DEFAULT_CELL.contents],
                          column: columnIndex + 1
                        });
                      });
                  }

                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if (cell.column > columnIndex) {
                      return Object.assign({}, cell, {
                        column: cell.column + 1,
                      })
                    } else if (cell.columnSpan > 1 && cell.column <= columnIndex && cell.column + cell.columnSpan - 1 >= columnIndex) {
                      return Object.assign({}, cell, {
                        columnSpan: cell.columnSpan + 1
                      });
                    }
                    if(cell.column === columnIndex && !cell.style.border.left) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            right: cell.style.border.left
                          })
                        })
                      })
                    }
                    return cell;
                  });
                  newCells = newCells.map(cell => {
                    if(!cell.style.border.right) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            left: cell.style.border.right
                          })
                        })
                      })
                    }
                    return cell;
                  });
                  if(newStoredElements[elementIndex].blockType === STATIC_TABLE_BLOCK) {
                    const columnCount = newStoredElements[elementIndex].style.tableProperties.columns + 1;
                    newStoredElements[elementIndex].style.tableProperties.columnWidths = Array(columnCount).fill(newStoredElements[elementIndex].style.width/columnCount);
                  } else {
                    newStoredElements[elementIndex].style.tableProperties.columnWidths
                    .splice(columnIndex, 0, newStoredElements[elementIndex].style.tableProperties.columnWidths[details.isLeft ? 0 : columnIndex - 1]);
                    newStoredElements[elementIndex].style.width = newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce((acc, col) => {
                      return acc + col;
                    }, 0);
                  }
                  newStoredElements[elementIndex].style.tableProperties.columns += 1;
                  newStoredElements[elementIndex].contents = [
                    ...newCells,
                    ...newStoredElements[elementIndex].contents
                  ];
                  updateElementStyles({ width: `${newStoredElements[elementIndex].style.width}px`, uuid: uuid });
                }
                // console.log("changes :: ", newStoredElements[elementIndex].style.tableProperties);

                isStyleChanged = true;
                break;
              case 'static-table-add-row':
                hasChanged = true;

                console.log({ details });

                if (details.index !== undefined) {
                  let rowIndex = details.index + (details.isTop ? 0 : 1);

                  let newCells = [];
                  let addingNewRow = true;

                  if (details.newElement) {
                    rowIndex = details.index + 1;
                    const newUUID = details.formUUID ? details.formUUID : uuidv4();
                    const adjacentCell = newStoredElements[elementIndex].contents.find(existingCell => {
                      return existingCell.row === rowIndex && existingCell.column === details.colIndex;
                    });
                    if (adjacentCell.rowSpan > 1) {
                      addingNewRow = false;
                    }
                    console.log({ adjacentCell });
                    newCells = [Object.assign({}, DEFAULT_CELL, {
                      uuid: newUUID,
                      contents: [Object.assign({}, details.newElement, { uuid: newUUID })],
                      column: details.colIndex,
                      row: rowIndex + (details.isTop ? 0 : adjacentCell.rowSpan + (addingNewRow ? 0 : -1)),
                      columnSpan: adjacentCell.columnSpan,
                      style: Object.assign({}, adjacentCell.style, {
                        border: Object.assign({}, adjacentCell.style.border, {
                          [details.isTop ? 'bottom' : 'top']: false
                        })
                      })
                    })];
                  } else if (details.isTop) {
                    const numberOfCols = newStoredElements[elementIndex].style.tableProperties.columnWidths.length;
                    newCells = (new Array(numberOfCols)).fill(0).map((e, columnIndex) => {
                      return Object.assign({}, DEFAULT_CELL, {
                        uuid: uuidv4(),
                        contents: details.isBlock ? [] : [...DEFAULT_CELL.contents],
                        column: columnIndex + 1,
                        row: rowIndex + 1 // TODO : Carry over the styles from the existing table??
                      });
                    });
                  } else {
                    newCells = newStoredElements[elementIndex].contents
                      .filter(cell => cell.row === rowIndex && cell.rowSpan === 1)
                      .map(cell => {
                        return Object.assign({}, cell, {
                          uuid: uuidv4(),
                          contents: details.isBlock ? [] : [...DEFAULT_CELL.contents],
                          row: rowIndex + 1
                        });
                      });
                  }

                  console.log({ rowIndex, columnIndex: details.colIndex});

                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    console.log("cell :: ", cell.row, " ", cell.column);
                    if ((cell.row > rowIndex) && addingNewRow) {
                      return Object.assign({}, cell, {
                        row: cell.row + 1,
                      })
                    } else if (details.newElement && cell.row === rowIndex) {
                      return Object.assign({}, cell, {
                        row: cell.row + (cell.column === details.colIndex && details.isTop ? 1 : 0),
                        rowSpan: cell.rowSpan +  (cell.column === details.colIndex
                          ? addingNewRow ? 0 : -1
                          : addingNewRow ? 1 : 0),
                        style: cell.column === details.colIndex ? Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            [details.isTop ? 'top' : 'bottom']: false
                          })
                        }) : cell.style
                      });
                    } else if (addingNewRow && cell.rowSpan > 1 && cell.row <= rowIndex && cell.row + cell.rowSpan - 1 >= rowIndex) {
                      return Object.assign({}, cell, {
                        rowSpan: cell.rowSpan + 1
                      });
                    }
                    if(cell.row === rowIndex && !cell.style.border.top) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            bottom: cell.style.border.top
                          })
                        })
                      })
                    }
                    return cell;
                  });
                if (!details.newElement) {
                  newCells = newCells.map(cell => {
                    if(!cell.style.border.bottom) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            top: cell.style.border.bottom
                          })
                        })
                      })
                    }
                    return cell;
                  })
                }

                  if (addingNewRow) {
                  newStoredElements[elementIndex].style.tableProperties.rowHeights
                    .splice(rowIndex, 0, newStoredElements[elementIndex].style.tableProperties.rowHeights[details.isTop ? 0 : rowIndex - 1]);
                  newStoredElements[elementIndex].style.tableProperties.rows += 1;
                  newStoredElements[elementIndex].style.height = newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, col) => {
                    return acc + col;
                  }, 0);
                  }

                  newStoredElements[elementIndex].contents = [
                    ...newCells,
                    ...newStoredElements[elementIndex].contents
                  ];
                  updateElementStyles({ height: `${newStoredElements[elementIndex].style.height}px`, uuid: uuid });
                }
                // console.log("changes :: ", newStoredElements[elementIndex].style.tableProperties);

                isStyleChanged = true;
                break;
              case 'static-table-duplicate-cols':
                hasChanged = true;

                console.log("static-table-duplicate-cols :: ", details);

                if (details.selectedRange?.start) {
                  let {
                    colStartIndex,
                    colEndIndex
                  } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);
                  colStartIndex += 1;
                  colEndIndex += 1;
                  const numberOfCols = Math.abs(colStartIndex - colEndIndex) + 1;
                  console.log({ colStartIndex, colEndIndex, numberOfCols });

                  let newCells = newStoredElements[elementIndex].contents
                      .filter(cell => cell.column >= colStartIndex && cell.column <= colEndIndex && cell.columnSpan <= numberOfCols)
                      .map(cell => {
                        const newCellUUID = uuidv4();
                        return Object.assign({}, cell, {
                          uuid: newCellUUID,
                          contents: cell.uuid,
                          column: cell.column + numberOfCols
                        });
                      });

                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if (cell.column > colEndIndex) {
                      return Object.assign({}, cell, {
                        column: cell.column + numberOfCols,
                      })
                    } else if (cell.columnSpan > 1 && cell.column <= colStartIndex && cell.column + cell.columnSpan - 1 >= colEndIndex) {
                      return Object.assign({}, cell, {
                        columnSpan: cell.columnSpan + numberOfCols
                      });
                    }
                    return cell;
                  });
                  const newColWidths = (new Array(numberOfCols)).fill(10).map((e, i) => newStoredElements[elementIndex].style.tableProperties.columnWidths[colStartIndex - 1 + i]);
                  let occupiedWidth = newColWidths.reduce((acc, colWidth) => acc + colWidth, 0);
                  let columnsLeft = newStoredElements[elementIndex].style.tableProperties.columns - numberOfCols;
                  if (occupiedWidth > 90) { // Divide the colum width to occupy 100%
                    console.log("coming here 1 ", occupiedWidth, columnsLeft);
                    newStoredElements[elementIndex].style.tableProperties.columnWidths.splice(
                      colEndIndex, 0, ...newColWidths
                    );
                    newStoredElements[elementIndex].style.tableProperties.columnWidths =
                      newStoredElements[elementIndex].style.tableProperties.columnWidths.map((colWidth, index) => {
                        return colWidth / 2;
                      });
                  } else { // Dive the occupied width by other columns to fit to 100%
                    console.log("coming here 2 ", newColWidths, occupiedWidth, columnsLeft);
                    for (let i = 0; i < newStoredElements[elementIndex].style.tableProperties.columns; i++) {
                      if (i >= colStartIndex - 1 && i <= colEndIndex - 1) {
                        continue;
                      }
                      const newWidth = newStoredElements[elementIndex].style.tableProperties.columnWidths[i] - (occupiedWidth / columnsLeft)
                      if (newWidth < 5) {
                        columnsLeft -= 1;
                      }
                    }
                    newStoredElements[elementIndex].style.tableProperties.columnWidths =
                      newStoredElements[elementIndex].style.tableProperties.columnWidths.map((colWidth, i) => {
                        if (i >= colStartIndex - 1 && i <= colEndIndex - 1) {
                          return colWidth;
                        }
                        if ((colWidth - (occupiedWidth / columnsLeft)) >= 5) {
                          return colWidth - (occupiedWidth / columnsLeft);
                        }
                        return colWidth;
                      })
                    newStoredElements[elementIndex].style.tableProperties.columnWidths.splice(
                      colEndIndex, 0, ...newColWidths
                    );
                  }

                  newStoredElements[elementIndex].style.tableProperties.columns += numberOfCols;
                  newStoredElements[elementIndex].contents = [
                    ...newCells,
                    ...newStoredElements[elementIndex].contents
                  ];
                  console.log({ newCells });
                }
                console.log("changes :: ", newStoredElements[elementIndex]);

                isStyleChanged = true;
                break;
              case 'static-table-duplicate-rows':
                hasChanged = true;

                console.log("static-table-duplicate-rows :: ", details);

                if (details.selectedRange?.start) {
                  let {
                    rowStartIndex,
                    rowEndIndex
                  } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);
                  rowStartIndex += 1;
                  rowEndIndex += 1;
                  const numberOfRows = Math.abs(rowEndIndex - rowStartIndex) + 1;
                  console.log({ numberOfRows });

                  let newCells = newStoredElements[elementIndex].contents
                      .filter(cell => cell.row >= rowStartIndex && cell.row <= rowEndIndex && cell.rowSpan <= numberOfRows)
                      .map(cell => {
                        const newCellUUID = uuidv4();
                        return Object.assign({}, cell, {
                          uuid: newCellUUID,
                          contents: cell.uuid,
                          row: cell.row + numberOfRows
                        });
                      });

                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if (cell.row > rowEndIndex) {
                      return Object.assign({}, cell, {
                        row: cell.row + numberOfRows,
                      })
                    } else if (cell.rowSpan > 1 && cell.row <= rowStartIndex && cell.row + cell.rowSpan - 1 >= rowEndIndex) {
                      return Object.assign({}, cell, {
                        rowSpan: cell.rowSpan + numberOfRows
                      });
                    }
                    return cell;
                  });
                  const newRowHeights = (new Array(numberOfRows)).fill(0).map((e, i) =>
                          newStoredElements[elementIndex].style.tableProperties.rowHeights[rowStartIndex - 1 + i]);
                  console.log({ newRowHeights });
                  newStoredElements[elementIndex].style.tableProperties.rowHeights
                    .splice(rowEndIndex - 1, 0, ...newRowHeights);
                  newStoredElements[elementIndex].style.tableProperties.rows += numberOfRows;
                  newStoredElements[elementIndex].style.height = newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, col) => {
                    return acc + col;
                  }, 0);

                  newStoredElements[elementIndex].contents = [
                    ...newCells,
                    ...newStoredElements[elementIndex].contents
                  ];
                  console.log({ newCells });
                }
                console.log("changes :: ", newStoredElements[elementIndex]);

                isStyleChanged = true;
                break;
              case 'replace-table-element-with-styles':
                hasChanged = true;
                newStoredElements[elementIndex] = JSON.parse(JSON.stringify(details.element));
                // newStoredElements[elementIndex].style.tableProperties.rowHeights = details.rowHeights;
                let groupedSpace = 0;
                if (details.rowHeights) {
                newStoredElements[elementIndex].style.tableProperties.rowHeights = newStoredElements[elementIndex].style.tableProperties.rowHeights.map((row, index) => {
                  if (details.rowHeights[index] === undefined || details.rowHeights[index] === 0) {
                    groupedSpace += row;
                    return row;
                  } else if (groupedSpace !== 0) {
                    const newHeight =  details.rowHeights[index] - groupedSpace;
                    groupedSpace = 0;
                    return newHeight;
                  }
                  return details.rowHeights[index];
                })
                }

                let changeInStyle = details.changeInStyle || {};

                if (newStoredElements[elementIndex]) {
                  newStoredElements[elementIndex].style.width = newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce((acc, col) => {
                    return acc + col;
                  }, 0);

                  changeInStyle = {
                    ...changeInStyle,
                    width: newStoredElements[elementIndex].style.width
                  }

                  newStoredElements[elementIndex].style.height = newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, row) => {
                    return acc + row;
                  }, 0);

                  changeInStyle = {
                    ...changeInStyle,
                    height: newStoredElements[elementIndex].style.height
                  }
                }

                updateElementStyles(Object.assign({}, changeInStyle, { uuid }));
                isStyleChanged = true;

                break;
              case 'static-table-resize-column':
                // hasChanged = true;
                console.log({ details, changed: newStoredElements[elementIndex] });

                if (details.index === 0 && details.translate) {  // Translate table x value is affected
                  newStoredElements[elementIndex].style.x -= details.diff;
                }

                const newWidth = newStoredElements[elementIndex].style.tableProperties.columnWidths[details.index] + details.diff;
                if (newWidth <= 0) {
                  details.prevent && details.prevent();
                  break;
                }

                newStoredElements[elementIndex].style.tableProperties.columnWidths.splice(
                  details.index, 1, newStoredElements[elementIndex].style.tableProperties.columnWidths[details.index] + details.diff
                );

                newStoredElements[elementIndex].style.width = newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce((acc, col) => {
                  return acc + col;
                }, 0);

                updateElementStyles({
                  width: `${newStoredElements[elementIndex].style.width}px`,
                  translateX: `${newStoredElements[elementIndex].style.x}px`,
                  uuid: uuid
                });

                details.refElement.dispatchEvent(new CustomEvent('updatingContentsForStyle', { detail: { newElement: newStoredElements[elementIndex] } }))

                // isStyleChanged = true;
                break;
              case 'static-table-resize-row':
                // hasChanged = true;

                if (details.index === 0 && details.translate) {  // Translate table y value is affected
                  newStoredElements[elementIndex].style.y -= details.diff;
                }

                newStoredElements[elementIndex].style.tableProperties.rowHeights.splice(
                  details.index, 1, newStoredElements[elementIndex].style.tableProperties.rowHeights[details.index] + details.diff
                );

                newStoredElements[elementIndex].style.height = newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, col) => {
                  return acc + col;
                }, 0);

                updateElementStyles({
                  height: `${newStoredElements[elementIndex].style.height}px`,
                  translateY: `${newStoredElements[elementIndex].style.y}px`,
                  uuid: uuid
                });

                details.refElement.dispatchEvent(new CustomEvent('updatingContentsForStyle', { detail: { newElement: newStoredElements[elementIndex] } }))

                // isStyleChanged = true;
                break;
              case 'static-table-delete-columns':
                let isColumn = true;
              case 'static-table-delete-rows':

              if (details.selectedRange?.start) {
                const {
                  rowStartIndex,
                  rowEndIndex,
                  colStartIndex,
                  colEndIndex
                } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);

                console.log({ rowStartIndex, rowEndIndex, colStartIndex, colEndIndex });
                const isSingleCell = details.selectedRange?.end === undefined;

                let prevent = false;
                if (isColumn) {
                  prevent = colEndIndex - colStartIndex + 1 === newStoredElements[elementIndex].style.tableProperties.columnWidths.length;
                } else {
                  prevent = rowEndIndex - rowStartIndex + 1 === newStoredElements[elementIndex].style.tableProperties.rowHeights.length;
                }
                if (prevent) {
                  details.prevent && details.prevent();
                  break;
                }

                hasChanged = true;

                let changeInStyle = {};

                if (isColumn) {
                  newStoredElements[elementIndex].style.tableProperties.columnWidths.splice(
                    colStartIndex,
                    (colEndIndex - colStartIndex) + 1
                  );
                  changeInStyle = {
                    width: newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce((acc, col) => {
                      return acc + col;
                    }, 0)
                  }
                } else {
                  newStoredElements[elementIndex].style.tableProperties.rowHeights.splice(
                    rowStartIndex,
                    (rowEndIndex - rowStartIndex) + 1
                  );
                  changeInStyle = {
                    height: newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, row) => {
                      return acc + row;
                    }, 0)
                  }
                }

                const rowDiff = (rowEndIndex - rowStartIndex) + 1;
                const colDiff = (colEndIndex - colStartIndex) + 1;

                newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                  if (isColumn) {
                    const deleteColumns = (new Array(colDiff)).fill(0).map((e, index) => colStartIndex + index);
                      if (cell.column < colStartIndex + 1) {
                        const colsInCell = (new Array(cell.columnSpan)).fill(0).map((e, index) => cell.column - 1 + index);
                        const containsColumns = cell.columnSpan > 1
                          ? colsInCell.filter(col => deleteColumns.includes(col)).length > 0
                          : false;
                        if (containsColumns) { // Cells with colSpan might still contain the deleted columns in them
                          return Object.assign({}, cell, {
                            columnSpan: cell.columnSpan - colsInCell.filter(col => deleteColumns.includes(col)).length
                          });
                        }
                        return cell;
                      } else if (cell.column > colEndIndex + 1) {
                        return Object.assign({}, cell, {
                          column: cell.column - colDiff
                        });
                      } else if (cell.columnSpan > 1) {
                        if (cell.column >= colStartIndex + 1 && cell.column + cell.columnSpan - 1 <= colEndIndex + 1) {
                          // Deleting cells which are fully within the range of deleted columns
                          return null;
                        }
                        // Cells might just partially contain the deleted rows
                        const colsInCell = (new Array(cell.columnSpan)).fill(0).map((e, index) => cell.column - 1 + index);
                        return Object.assign({}, cell, {
                          columnSpan: cell.columnSpan - colsInCell.filter(col => deleteColumns.includes(col)).length
                        });
                      }
                      // Fully deleted cell
                      return null;
                  } else {
                    const deleteRows = (new Array(rowDiff)).fill(0).map((e, index) => rowStartIndex + index);
                      if (cell.row < rowStartIndex + 1) {
                        const rowsInCell = (new Array(cell.rowSpan)).fill(0).map((e, index) => cell.row - 1 + index);
                        const containsRows = cell.rowSpan > 1
                          ? rowsInCell.filter(row => deleteRows.includes(row)).length > 0
                          : false;
                        if (containsRows) { // Cells with rowSpan might still contain the deleted rows in them
                          return Object.assign({}, cell, {
                            rowSpan: cell.rowSpan - rowsInCell.filter(row => deleteRows.includes(row)).length
                          });
                        }
                        return cell;
                      } else if (cell.row > rowEndIndex + 1) {
                        return Object.assign({}, cell, {
                          row: cell.row - rowDiff
                        });
                      } else if (cell.rowSpan > 1) {
                        if (cell.row >= rowStartIndex + 1 && cell.row + cell.rowSpan - 1 <= rowEndIndex + 1) {
                          // Deleting cells which are fully within the range of deleted rows
                          return null;
                        }
                        // Cells might just partially contain the deleted rows
                        const rowsInCell = (new Array(cell.rowSpan)).fill(0).map((e, index) => cell.row - 1 + index);
                        return Object.assign({}, cell, {
                          rowSpan: cell.rowSpan - rowsInCell.filter(row => deleteRows.includes(row)).length
                        });
                      }
                      // Fully deleted cell
                      return null;
                  }
                }).filter(cell => cell !== null);

                newStoredElements[elementIndex].style = {
                  ...newStoredElements[elementIndex].style,
                  ...changeInStyle
                }

                updateElementStyles({
                  width: `${newStoredElements[elementIndex].style.width}px`,
                  height: `${newStoredElements[elementIndex].style.height}px`,
                  uuid: uuid
                });

                isStyleChanged = true;

                // Expecting only columns and rows with definitive widths and heights to be deleted, not ones which are merged
                // details.refElement.dispatchEvent(new CustomEvent('updatingContentsForStyle', { detail: { newElement: newStoredElements[elementIndex] } }))
              }
              break;
            case 'static-table-merge-cells':
              if (details.selectedRange?.start) {
                hasChanged = true;
                const {
                  rowStartIndex,
                  rowEndIndex,
                  colStartIndex,
                  colEndIndex
                } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);

                console.log({ rowStartIndex, rowEndIndex, colStartIndex, colEndIndex });

                newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                  if (cell.row === rowStartIndex + 1 && cell.column === colStartIndex + 1) { // Start Cell
                    return Object.assign({}, cell, {
                      rowSpan: rowEndIndex - rowStartIndex + 1,
                      columnSpan: colEndIndex - colStartIndex + 1
                    });
                  } else if ((rowStartIndex !== rowEndIndex ? (cell.row >= rowStartIndex + 1) && (cell.row <= rowEndIndex + 1) : cell.row === rowStartIndex + 1) &&
                    (colStartIndex !== colEndIndex ? (cell.column >= colStartIndex + 1) && (cell.column <= colEndIndex + 1) : cell.column === colEndIndex + 1)) {
                    return -1;
                  }
                  return cell;
                }).filter(cell => cell !== -1);

                console.log("Merge changes :: ", newStoredElements[elementIndex].contents);
              }
              break;
            case 'static-table-split-cells':
              if (details.selectedRange?.start) {
                // hasChanged = true;

                let mergedCells = [];

                if (details.selectedRange?.end) {

                  const {
                    rowStartIndex,
                    rowEndIndex,
                    colStartIndex,
                    colEndIndex
                  } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);

                  mergedCells = newStoredElements[elementIndex].contents.filter(cell => {
                    if ((cell.row >= rowStartIndex + 1 && cell.row <= rowEndIndex + 1) &&
                      (cell.column >= colStartIndex + 1 && cell.column <= colEndIndex + 1)) {
                      return cell.rowSpan !== 1 || cell.columnSpan !== 1;
                    }
                    return false;
                  });

                } else {
                  mergedCells = newStoredElements[elementIndex].contents.filter(cell => {
                    if (cell.uuid === details.selectedRange.start) {
                      return cell.rowSpan !== 1 || cell.columnSpan !== 1;
                    }
                    return false;
                  });
                }

                console.log("selected cells for unmerge :: ", mergedCells);

                const extraCells = mergedCells.reduce((acc, cell) => {
                  if (cell.columnSpan > 1) {
                    (new Array(cell.columnSpan - 1)).fill(0).forEach((e, index) => {
                      acc.push(Object.assign({}, cell, {
                        column: cell.column + index + 1,
                        columnSpan: 1,
                        rowSpan: 1,
                        contents: details.isBlock ? [] : [...DEFAULT_CELL.contents]
                      }));
                    });
                  }
                  if (cell.rowSpan > 1) {
                    let splitCells = acc.filter(extraCell => extraCell.row === cell.row && (
                      extraCell.column >= cell.column && extraCell.column <= cell.column + cell.columnSpan
                    ));
                    splitCells = [cell, ...splitCells];
                    console.log({ splitCells });
                    (new Array(cell.rowSpan - 1)).fill(0).forEach((e, index) => {
                      splitCells.forEach(splitCell => {
                        acc.push(Object.assign({}, splitCell, {
                          row: splitCell.row + index + 1,
                          columnSpan: 1,
                          rowSpan: 1,
                          contents: details.isBlock ? [] : [...DEFAULT_CELL.contents]
                        }));
                      });
                    });
                  }
                  return acc;
                }, []);

                newStoredElements[elementIndex].contents = [
                  ...newStoredElements[elementIndex].contents.map(existingCell => {
                    if (mergedCells.find(mergedCell => mergedCell.uuid === existingCell.uuid)) {
                      return Object.assign({}, existingCell, {
                        columnSpan: 1,
                        rowSpan: 1,
                      });
                    }
                    return existingCell;
                  }),
                  ...extraCells.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
                ];

                console.log("Split changes :: ", newStoredElements[elementIndex].contents, extraCells);

                details.refElement.dispatchEvent(new CustomEvent('updatingContentsForStyle', { detail: { newElement: newStoredElements[elementIndex] } }))
              }
              break;
            case 'static-table-formatting':
              hasChanged = true;
              if (details.selectedRange?.start) {
                let {
                  rowStartIndex,
                  rowEndIndex,
                  colStartIndex,
                  colEndIndex
                } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange);

                rowStartIndex++;
                rowEndIndex++;
                colStartIndex++;
                colEndIndex++;
                console.log({ rowStartIndex, rowEndIndex, colStartIndex, colEndIndex });

                if (details.format === 'background') {
                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if ((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                        (cell.column >= colStartIndex && cell.column <= colEndIndex)) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          backgroundColor: details.value
                        })
                      })
                    }
                    return cell;
                  });
                } else if (details.format === 'borderColor') {
                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if ((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                        (cell.column >= colStartIndex && cell.column <= colEndIndex)) {
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          border: Object.assign({}, cell.style.border, {
                            borderColor: details.value
                          })
                        })
                      })
                    }
                    return cell;
                  });
                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    const style = { ...cell.style };
                    const border =  { ...cell.style.border };
                    if(cell.row - 1 === rowEndIndex && cell.column >= colStartIndex && cell.column <= colEndIndex) {
                      border.top = false;
                    }
                    if (cell.row + 1 === rowStartIndex && cell.column >= colStartIndex && cell.column <= colEndIndex) {
                      border.bottom = false;
                    }
                    if(cell.column - 1 === colEndIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex) { 
                      border.left = false;
                    }
                    if(cell.column + 1 === colStartIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex) { 
                      border.right = false;
                    }
                    if(cell.row >= rowStartIndex && cell.row <= rowEndIndex && cell.column >= colStartIndex && cell.column <= colEndIndex) {
                      border.top = true;
                      border.bottom = true;
                      border.left = true;
                      border.right = true;
                    }
                    return { ...cell, style: { ...style, border }};  
                  });
                } else if (details.format === 'toggleBorder') {
                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    switch(details.key) {
                      case 'hide-top':
                      case 'show-top':
                        const topBorder = details.key === "show-top";
                        if((cell.row + 1 >= rowStartIndex) && (((cell.row - 1) + cell.rowSpan) < rowEndIndex)
                          && (cell.column >= colStartIndex) && (cell.column <= colEndIndex))
                            return Object.assign({}, cell, {
                              style: Object.assign({}, cell.style, {
                                border: Object.assign({}, cell.style.border, {
                                  bottom: topBorder,
                                })
                              })
                            })
                          return cell;
                      case 'hide-bottom':
                      case 'show-bottom':
                        const bottomBorder = details.key === "show-bottom";
                        if((cell.row > rowStartIndex) && (cell.row - 1 <= rowEndIndex)
                          && (cell.column >= colStartIndex) && (cell.column <= colEndIndex))
                            return Object.assign({}, cell, {
                              style: Object.assign({}, cell.style, {
                                border: Object.assign({}, cell.style.border, {
                                  top: bottomBorder,
                                })
                              })
                            })
                          return cell;
                      case 'hide-left':
                      case 'show-left':
                        const leftBorder = details.key === "show-left";
                        if((cell.row >= rowStartIndex) && (cell.row <= rowEndIndex)
                          && (cell.column + 1 >= colStartIndex) && (((cell.column - 1) + cell.columnSpan) < colEndIndex))
                            return Object.assign({}, cell, {
                              style: Object.assign({}, cell.style, {
                                border: Object.assign({}, cell.style.border, {
                                  right: leftBorder,
                                })
                              })
                            })
                          return cell;
                      case 'hide-right':
                      case 'show-right':
                        const rightBorder = details.key === "show-right";
                        if((cell.row >= rowStartIndex) && (cell.row <= rowEndIndex)
                          && (cell.column > colStartIndex) && (cell.column - 1 <= colEndIndex))
                            return Object.assign({}, cell, {
                              style: Object.assign({}, cell.style, {
                                border: Object.assign({}, cell.style.border, {
                                  left: rightBorder,
                                })
                              })
                            })
                          return cell;
                      case 'hide-all-inner':
                      case 'show-all-inner':
                        const innerBorder = details.key === 'show-all-inner';
                        return Object.assign({}, cell, {
                            style: Object.assign({}, cell.style, {
                              border: Object.assign({}, cell.style.border, {                                
                                ...((cell.row > rowStartIndex && cell.row <= rowEndIndex) &&
                                (cell.column >= colStartIndex && cell.column <= colEndIndex) && { top: innerBorder }),
                                ...((cell.row >= rowStartIndex && ((cell.row - 1) + cell.rowSpan) < rowEndIndex) &&
                                (cell.column >= colStartIndex && cell.column <= colEndIndex) && { bottom: innerBorder }),
                                ...((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                                (cell.column > colStartIndex && cell.column <= colEndIndex) && { left: innerBorder }),
                                ...((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                                (cell.column >= colStartIndex && ((cell.column - 1) + cell.columnSpan) < colEndIndex) && { right: innerBorder }),
                                })
                              })
                            })
                      case 'hide-all-outer':
                      case 'show-all-outer':
                        const outerBorder = details.key === 'show-all-outer';
                        return Object.assign({}, cell, {
                            style: Object.assign({}, cell.style, {
                              border: Object.assign({}, cell.style.border, {
                                ...(((cell.row === rowStartIndex && cell.column >= colStartIndex && cell.column <= colEndIndex) || 
                                  (cell.row - 1 === rowEndIndex && cell.column >= colStartIndex && cell.column <= colEndIndex)) && { top: outerBorder }),
                                ...(((cell.row + 1 === rowStartIndex && cell.column >= colStartIndex && cell.column <= colEndIndex) ||
                                  (((cell.row - 1) + cell.rowSpan) === rowEndIndex && cell.column >= colStartIndex && cell.column <= colEndIndex)) && { bottom: outerBorder }),
                                ...(((cell.column - 1 === colEndIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex) ||
                                  (cell.column === colStartIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex)) && { left: outerBorder }),
                                ...(((cell.column + 1 === colStartIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex) ||
                                  (((cell.column - 1) + cell.columnSpan) === colEndIndex && cell.row >= rowStartIndex && cell.row <= rowEndIndex)) && { right: outerBorder }),
                              })
                            })
                          })
                      default: return '';
                    }
                  });
                  const allBorderKeys = ['show-all-inner', 'hide-all-inner', 'hide-all-outer', 'show-all-outer'];
                  if(!allBorderKeys.includes(details.key)) {
                    newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                      if ((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                          (cell.column >= colStartIndex && cell.column <= colEndIndex)) {
                        return Object.assign({}, cell, {
                          style: Object.assign({}, cell.style, {
                            border: Object.assign({}, cell.style.border, {
                              [details.key.split("-")[1]]: details.value
                            })
                          })
                        })
                      }
                      return cell;
                    });
                  }
                } else if (details.format === 'padding') {
                  console.log({ details });

                  hasChanged = false;

                  const newRows = Array.from(details.tableRef.current.querySelectorAll('tr')).map(el => el);
                  const columnWidths = newStoredElements[elementIndex].style.tableProperties.columnWidths;
                  const rowHeights = newStoredElements[elementIndex].style.tableProperties.rowHeights;
                  const affectedRows = {};
                  newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    if ((cell.row >= rowStartIndex && cell.row <= rowEndIndex) &&
                        (cell.column >= colStartIndex && cell.column <= colEndIndex)) {
                      affectedRows[cell.row - 1] = (details.value.top + details.value.bottom) - (cell.style.padding.top + cell.style.padding.bottom);
                      /*if (cell.contents.length > 1) {
                        const originalDOM = newRows[cell.row - 1].childNodes[cell.column - 1].childNodes[0]; // ql-container
                        const newDOM = originalDOM.cloneNode(true);
                        newDOM.style.position = 'absolute';
                        newDOM.style.visibility = 'hidden';
                        newDOM.style.width = `${columnWidths[cell.column - 1] - details.value.left - details.value.right}px`;
                        document.body.appendChild(newDOM);
                        if (newDOM.clientHeight !== originalDOM.clientHeight || details.value.top !== cell.style.padding.top || details.value.bottom !== cell.style.padding.bottom) {
                          // console.log("Max calculation :: ", affectedRows[cell.row - 1], rowHeights[cell.row - 1], newDOM.clientHeight + details.value.top + details.value.bottom + 1);
                          affectedRows[cell.row - 1] = Math.max(affectedRows[cell.row - 1] || 0, rowHeights[cell.row - 1], newDOM.clientHeight + details.value.top + details.value.bottom + 1);
                        }
                        // console.log({ newDOM });
                        document.body.removeChild(newDOM);
                      }*/
                      return Object.assign({}, cell, {
                        style: Object.assign({}, cell.style, {
                          padding: details.value
                        })
                      })
                    }
                    return cell;
                  });
                  // console.log({ affectedRows });
                  newStoredElements[elementIndex].style.tableProperties.rowHeights = newStoredElements[elementIndex].style.tableProperties.rowHeights.map((rowHeight, rowIndex) => {
                    if (affectedRows[rowIndex] !== undefined) {
                      return rowHeight + affectedRows[rowIndex];
                    }
                    return rowHeight;
                  });
                  // newStoredElements[elementIndex].style.tableProperties.rowHeights = newRowHeights;

                  newStoredElements[elementIndex].style.height = newStoredElements[elementIndex].style.tableProperties.rowHeights.reduce((acc, col) => {
                    return acc + col;
                  }, 0);

                  /*updateElementStyles({ height: `${newStoredElements[elementIndex].style.height}px`, uuid: uuid });
                  isStyleChanged = true;*/

                  details.tableRef.current.dispatchEvent(new CustomEvent('updatingContentsForStyle', { detail: { newElement: newStoredElements[elementIndex] } }))

                  // console.log("Changes after padding :: ", newStoredElements[elementIndex].style.tableProperties.rowHeights, storedElements[elementIndex].style.tableProperties.rowHeights);
                }
              }
              break;
            case 'static-block-resize-column':
              // Checking if column width is accommodated within current flow dimensions and padding of the same block
              const MIN_COLUMN_WIDTH = 2;
              const pageDetails = details.pageDetails;
              const fullWidth = pageDetails.width - pageDetails.margin[1] - pageDetails.margin[3] - (newStoredElements[elementIndex].style.padding?.left || 0) - (newStoredElements[elementIndex].style.padding?.right || 0);

              const differenceInWidth = parseFloat(((details.diff / fullWidth) * 100).toFixed(3));
              console.log({ differenceInWidth, details });
              const newColumns = [...newStoredElements[elementIndex].style.tableProperties.columnWidths];

              newColumns[details.index] += differenceInWidth;

              if (newColumns[details.index] <= MIN_COLUMN_WIDTH) {
                console.log("calling prompt !!! ", newColumns[details.index]);
                if (details.prompt) details.prompt();
                return { hasChanged: false, elements: [] };
              }

              let tableWidth = newColumns.reduce((acc, col) => acc + col, 0);

              // Checking if change in width can be adjusted in the next column's width
              if (newColumns[details.index + 1] !== undefined) {
                const nextColumnWidth = newColumns[details.index + 1] - differenceInWidth;
                if (nextColumnWidth <= MIN_COLUMN_WIDTH) {
                  if (details.prevent) details.prevent();
                  return { hasChanged: false, elements: [] };
                } else {
                  newColumns[details.index + 1] = nextColumnWidth;

                  tableWidth = parseFloat((newColumns.reduce((acc, col) => acc + col, 0)).toFixed(2));
                  console.log({ tableWidth, newColumns }, "2")

                  if (tableWidth > 100) {
                    if (details.prevent) details.prevent();
                    return { hasChanged: false, elements: [] };
                  }
                }
              } else {
                console.log("Should not come here ideally since last column resize is disabled in design");
                if (details.prevent) details.prevent();
                return { hasChanged: false, elements: [] };
              }
              newStoredElements[elementIndex].style.tableProperties.columnWidths = newColumns;
              console.log("Changes in resize :: ", newColumns);
              hasChanged = true;
              break;
            case 'static-block-add-column':
              hasChanged = true;

              if (details.index !== undefined) {
                let columnIndex = details.index + (details.isLeft ? 0 : 1);

                let newCells = [];
                let addingNewCol = true;

                if (details.newElement && details.rowIndex) { // Split cells logic
                  columnIndex = details.index + 1;
                  const newUUID = uuidv4();

                  const adjacentCell = newStoredElements[elementIndex].contents.find(existingCell => {
                    return existingCell.row === details.rowIndex && existingCell.column === columnIndex;
                  });
                  if (adjacentCell.columnSpan > 1) {
                    addingNewCol = false;
                  }
                  console.log({ adjacentCell, columnIndex, details });
                  newCells = [Object.assign({}, DEFAULT_CELL, {
                    uuid: newUUID,
                    contents: [Object.assign({}, details.newElement, { uuid: newUUID })],
                    column: columnIndex + (details.isLeft ? 0 : (adjacentCell.columnSpan + (addingNewCol ? 0 : -1))),
                    row: details.rowIndex,
                    rowSpan: adjacentCell.rowSpan,
                    style: Object.assign({}, adjacentCell.style, {
                      border: Object.assign({}, adjacentCell.style.border, {
                        [details.isLeft ? 'right' : 'left']: false
                      })
                    })
                  })];
                } else if (details.isLeft) {
                  const numberOfRows = newStoredElements[elementIndex].style.tableProperties.rowHeights.length;
                  newCells = (new Array(numberOfRows)).fill(0).map((e, rowIndex) => {
                    return Object.assign({}, DEFAULT_CELL, {
                      uuid: uuidv4(),
                      contents: [],
                      column: columnIndex + 1, // Column index starts with 1 not 0
                      row: rowIndex + 1, // TODO : Carry over the styles from the existing table??
                    })
                  });
                } else {
                  newCells = newStoredElements[elementIndex].contents
                    .filter(cell => cell.column === columnIndex && cell.columnSpan === 1)
                    .map(cell => {
                      return Object.assign({}, cell, {
                        uuid: uuidv4(),
                        contents: [],
                        column: columnIndex + 1
                      });
                    });
                }

                console.log({ columnIndex, rowIndex: details.rowIndex });

                newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                  console.log("cell :: ", cell.row, " ", cell.column);
                  if ((cell.column > columnIndex) && addingNewCol) {
                    console.log("Coming here 1");
                    return Object.assign({}, cell, {
                      column: cell.column + 1,
                    })
                  } else if (details.newElement && cell.column === columnIndex) {
                    return Object.assign({}, cell, {
                      column: cell.column + (cell.row === details.rowIndex && addingNewCol &&  details.isLeft ? 1 : 0),
                      columnSpan: cell.columnSpan + (cell.row === details.rowIndex
                        ? addingNewCol ? 0 : -1
                        : addingNewCol ? 1 : 0),
                      style: cell.row === details.rowIndex ? Object.assign({}, cell.style, {
                        border: Object.assign({}, cell.style.border, {
                          [details.isLeft ? 'left' : 'right']: false
                        })
                      }) : cell.style
                    });
                  } else if (addingNewCol && cell.columnSpan > 1 && cell.column <= columnIndex && cell.column + cell.columnSpan - 1 >= columnIndex) {
                    return Object.assign({}, cell, {
                      columnSpan: cell.columnSpan + 1
                    });
                  }
                  if(cell.column === columnIndex && !cell.style.border.left) {
                    return Object.assign({}, cell, {
                      style: Object.assign({}, cell.style, {
                        border: Object.assign({}, cell.style.border, {
                          right: cell.style.border.left
                        })
                      })
                    })
                  }
                  return cell;
                });
                if (!details.newElement) {
                newCells = newCells.map(cell => {
                  if(!cell.style.border.right) {
                    return Object.assign({}, cell, {
                      style: Object.assign({}, cell.style, {
                        border: Object.assign({}, cell.style.border, {
                          left: cell.style.border.right
                        })
                      })
                    })
                  }
                  return cell;
                });
                }
                if (addingNewCol) {
                const columnCount = newStoredElements[elementIndex].style.tableProperties.columns + 1;

                /*const newWidth = 100 / columnCount;
                newStoredElements[elementIndex].style.tableProperties.columnWidths = Array(columnCount).fill(newWidth);*/
                newStoredElements[elementIndex].style.tableProperties.columnWidths =
                  newStoredElements[elementIndex].style.tableProperties.columnWidths.map(colWidth => {
                    return colWidth * 0.9;
                  });
                /*newStoredElements[elementIndex].style.tableProperties.columnWidths
                    .splice(columnIndex, 0, newStoredElements[elementIndex].style.tableProperties.columnWidths[details.isLeft ? 0 : columnIndex - 1]);*/
                newStoredElements[elementIndex].style.tableProperties.columnWidths
                                    .splice(columnIndex, 0, 10);
                newStoredElements[elementIndex].style.tableProperties.columns = columnCount;
                }

                newStoredElements[elementIndex].contents = [
                  ...newCells,
                  ...newStoredElements[elementIndex].contents
                ];
                console.log("Change in contents :: ", newCells, newStoredElements[elementIndex].contents);
              }
              break;
            case 'static-block-delete-columns':
              if (details.selectedRange?.start) {
                const {
                  rowStartIndex,
                  rowEndIndex,
                  colStartIndex,
                  colEndIndex
                } = getRangeOfStaticTable(newStoredElements[elementIndex], details.selectedRange, true);

                const isSingleCell = details.selectedRange?.end === undefined;

                let prevent = false;
                prevent = colEndIndex - colStartIndex + 1 === newStoredElements[elementIndex].style.tableProperties.columnWidths.length;
                if (prevent) {
                  details.prevent && details.prevent();
                  break;
                }

                hasChanged = true;

                // TODO : Deleted column's Width has to be added to the previous column
                const deletedWidth = newStoredElements[elementIndex].style.tableProperties.columnWidths.reduce(
                  (acc, colWidth, i) => {
                    if (isSingleCell ? i === colStartIndex : i >= colStartIndex && i <= colEndIndex) {
                      return acc + colWidth;
                    }
                    return acc;
                  },
                  0
                );

                let leftOverIndex;
                if (newStoredElements[elementIndex].style.tableProperties.columnWidths[colStartIndex - 1]) {
                   leftOverIndex = colStartIndex - 1;
                } else if (newStoredElements[elementIndex].style.tableProperties.columnWidths[colEndIndex]) {
                   leftOverIndex = colStartIndex;
                }
                newStoredElements[elementIndex].style.tableProperties.columnWidths = newStoredElements[elementIndex].style.tableProperties.columnWidths
                  .map((colWidth, i) => {
                    if (isSingleCell ? i === colStartIndex : i >= colStartIndex && i <= colEndIndex) {
                      return undefined;
                    }
                    return colWidth;
                  })
                  .filter(colWidth => colWidth !== undefined);
                  /*.map((colWidth, i) => {
                    if (i === leftOverIndex) {
                      return colWidth + deletedWidth;
                    }
                    return colWidth;
                  });*/
                const remainingColWidth = 100 - deletedWidth;
                newStoredElements[elementIndex].style.tableProperties.columnWidths =
                  newStoredElements[elementIndex].style.tableProperties.columnWidths.map(colWidth => {
                    return colWidth * 100 / remainingColWidth;
                  })

                const rowDiff = (rowEndIndex - rowStartIndex) + 1;
                const colDiff = (colEndIndex - colStartIndex) + 1;

                newStoredElements[elementIndex].style.tableProperties.columns -= colDiff;

                console.log({ leftOverIndex, deletedWidth, changes: newStoredElements[elementIndex] });

                newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                    const deleteColumns = (new Array(colDiff)).fill(0).map((e, index) => colStartIndex + index);
                      if (cell.column < colStartIndex + 1) {
                        const colsInCell = (new Array(cell.columnSpan)).fill(0).map((e, index) => cell.column - 1 + index);
                        const containsColumns = cell.columnSpan > 1
                          ? colsInCell.filter(col => deleteColumns.includes(col)).length > 0
                          : false;
                        if (containsColumns) { // Cells with colSpan might still contain the deleted columns in them
                          return Object.assign({}, cell, {
                            columnSpan: cell.columnSpan - colsInCell.filter(col => deleteColumns.includes(col)).length
                          });
                        }
                        return cell;
                      } else if (cell.column > colEndIndex + 1) {
                        return Object.assign({}, cell, {
                          column: cell.column - colDiff
                        });
                      } else if (cell.columnSpan > 1) {
                        if (cell.column >= colStartIndex + 1 && cell.column + cell.columnSpan - 1 <= colEndIndex + 1) {
                          // Deleting cells which are fully within the range of deleted columns
                          return null;
                        }
                        // Cells might just partially contain the deleted rows
                        const colsInCell = (new Array(cell.columnSpan)).fill(0).map((e, index) => cell.column - 1 + index);
                        return Object.assign({}, cell, {
                          columnSpan: cell.columnSpan - colsInCell.filter(col => deleteColumns.includes(col)).length
                        });
                      }
                      // Fully deleted cell
                      return null;
                }).filter(cell => cell !== null);
              }
              break;
            case 'static-table-drop-over-cell':
              hasChanged = true;
              newStoredElements[elementIndex].contents = newStoredElements[elementIndex].contents.map(cell => {
                if (cell.uuid === details.uuid) {
                  return Object.assign({}, cell, {
                    contents: [details.newElement]
                  });
                }
                return cell;
              });
              break;
            default:
              break;
          }
        }
        if (hasChanged) {
          handlePageHistory([newStoredElements[elementIndex]]);
          let extraChanges = {
            before: key ? storedElements[elementIndex][key] : storedElements[elementIndex],
            after: key ? newStoredElements[elementIndex][key] : newStoredElements[elementIndex]
          };
          if (textChanges) {
            extraChanges = {
              ...extraChanges,
              undoLength: details.undoDiff,
              redoLength: details.redoDiff,
              textOperation: details.op,
              isGroupHeader: key === 'groupHeaderContent',
              isRow: details.isRow
            }
          }
          return {
            elements: newStoredElements,
            elementUUID: uuid,
            textChanges,
            tableChanges: isTableStyleChanged,
            hasChanged,
            ...extraChanges,
            callback: isStyleChanged ? updateElementStyles : undefined,
            extraFormElementsUuids : details.extraFormElementsUuids,
            ...(details.nestedChanges || {}),
          };
        } else {
          console.log("Stored elements not changed :: !!!");
          return {
            elements: storedElements,
            hasChanged
          };
        }
      });
    }, []
  )

  const updateStoredElementDetails = useCallback(
    (elementId, details, updationType, fillableElement) => {
     setElementsStore(storedElements => {
      let uuid = elementId;
      if (elementId.includes('overflow-table')) {
        uuid = elementId.split('_')[1];
        updationType = "overflow-table-styles";
      }
      const elementIndex = storedElements.findIndex(element => element.uuid === uuid);
      let newStoredElements = JSON.parse(JSON.stringify(storedElements)); // Deep copying an Object
      let elementWidth = 0;
      if (elementIndex >= 0) {
        switch (updationType) {
          case 'text-value':
            newStoredElements[elementIndex].elementDetails.value = details;
            break;
          case 'styles':
            newStoredElements[elementIndex].elementDetails.styles = {
              ...newStoredElements[elementIndex].elementDetails.styles,
              ...details,
            };
            break;
          case 'svg-value':
            newStoredElements[elementIndex].elementDetails.value = details;
            break;
          case 'svg-value-with-styles':
            const updatedStyles = details.styles;
            newStoredElements[elementIndex].elementDetails.value = details.value;
            newStoredElements[elementIndex].elementDetails.styles = {
              ...newStoredElements[elementIndex].elementDetails.styles,
              ...updatedStyles,
            };
            break;
          case 'svg-colors':
            newStoredElements[elementIndex].elementDetails.svgColors = details;
            break;
          case 'details':
            newStoredElements[elementIndex].elementDetails = details;
            if(fillableElement){
              newStoredElements[elementIndex].parentPageId = fillableElement.parentPageId;
            }
            break;
          case 'extra-details':
            newStoredElements[elementIndex].elementDetails = {
               ...newStoredElements[elementIndex].elementDetails,
               ...details
            };
            break;
          case 'barcode':
            let { dataValue, url } = details;
            newStoredElements[elementIndex].elementDetails['barcodeValue'] = url;
            newStoredElements[elementIndex].elementDetails['value'] = dataValue;
            break;
          case 'group-styles':
            const { elementStyles, subGroupStyles } = details;
            newStoredElements[elementIndex].elementDetails.styles = { ...newStoredElements[elementIndex].elementDetails.styles, ...elementStyles };
            newStoredElements[elementIndex].elementDetails.elements.value = newStoredElements[elementIndex].elementDetails.elements.value.map((subGroupEl, subIndex) => {
              subGroupEl.elementDetails.styles = { ...subGroupEl.elementDetails.styles, ...subGroupStyles[subIndex].styles };
              const storedIndex = storedElements.findIndex(storedEl => storedEl.uuid === subGroupEl.uuid);
              newStoredElements[storedIndex].elementDetails.styles = { ...newStoredElements[storedIndex].elementDetails.styles, ...subGroupStyles[subIndex].styles };
              if (subGroupEl.elementDetails.type === 'text') {
                subGroupEl.elementDetails.value = subGroupStyles[subIndex].value;
                newStoredElements[storedIndex].elementDetails.value = subGroupStyles[subIndex].value;
              }
              return subGroupEl;
            });
            updateElementStyles({ uuid, height: elementStyles.height, width: elementStyles.width, override: true });
            break;
          case 'radio-button-select':
            newStoredElements[elementIndex].elementDetails.elements.value = newStoredElements[elementIndex].elementDetails.elements.value.map((subGroupEl, subIndex) => {
              if (subGroupEl.uuid === details.uuid) {
                subGroupEl.elementDetails.value.formField.checked = true;
                const storedIndex = storedElements.findIndex(storedEl => storedEl.uuid === subGroupEl.uuid);
                newStoredElements[storedIndex].elementDetails.value.formField.checked = true;
              } else if (subGroupEl.elementDetails.type === 'radiobutton') {
                subGroupEl.elementDetails.value.formField.checked = false;
                const storedIndex = storedElements.findIndex(storedEl => storedEl.uuid === subGroupEl.uuid);
                newStoredElements[storedIndex].elementDetails.value.formField.checked = false;
              }
              return subGroupEl;
            });
            break;
          case 'checkbox-styles':
            newStoredElements[elementIndex].elementDetails.elements.value = newStoredElements[elementIndex].elementDetails.elements.value.map((subGroupEl, subIndex) => {
              if (subGroupEl.elementDetails.type === 'checkBox' || subGroupEl.elementDetails.type === 'radiobutton') {
                subGroupEl.elementDetails.styles = { ...subGroupEl.elementDetails.styles, ...details };
                const storedIndex = storedElements.findIndex(storedEl => storedEl.uuid === subGroupEl.uuid);
                newStoredElements[storedIndex].elementDetails.styles = { ...newStoredElements[storedIndex].elementDetails.styles, ...details };
              }
              return subGroupEl;
            });
            break;
            case 'combobox-styles':
              newStoredElements[elementIndex].elementDetails.styles = {
                ...newStoredElements[elementIndex].elementDetails.styles,
                ...details,
              };
            break;
          case 'group-el-styles':
            const { uuid, height: elHeight } = details;
            const storedElement = storedElements.find(storedEl => storedEl.uuid === uuid);
            // TODO : removed height responsiveness of element - figure out if this is needed?
            /*const heightDiff = elHeight - parseFloat(storedElement.elementDetails.styles.height.replace('px', ''));
            newStoredElements[elementIndex].elementDetails.styles.height = `${parseFloat(newStoredElements[elementIndex].elementDetails.styles.height.replace('px', '')) + heightDiff}px`;*/
            newStoredElements[elementIndex].elementDetails.elements.value = newStoredElements[elementIndex].elementDetails.elements.value.map((subGroupEl, subIndex) => {
              const storedIndex = storedElements.findIndex(storedEl => storedEl.uuid === subGroupEl.uuid);
              if (subGroupEl.uuid === uuid) {
                newStoredElements[storedIndex].elementDetails.styles.height = `${elHeight}px`;
                subGroupEl.elementDetails.styles.height = `${elHeight}px`;
              } else {
                // TODO : Find out elements below the height of selected editor and move them down
              }
              return subGroupEl;
            });
            updateElementStyles({ uuid, height: `${elHeight}px`, override: true });
            break;
          case 'group-details':
            const { elementDetails, newElements, updateElements  } = details;
            newStoredElements[elementIndex].elementDetails = elementDetails;
            if (newElements) {
              newStoredElements = newStoredElements.concat(newElements);
            } else if (updateElements) {
              updateElements.forEach(storedEl => {
                const subElementIndex = storedElements.findIndex(el => el.uuid === storedEl.uuid);
                newStoredElements[subElementIndex].elementDetails.styles = storedEl.elementDetails.styles;
              });
            }
            let newStyles = {
              uuid: elementId,
              height: parseInt(`${elementDetails.styles.height}`.replace('px', '')),
              width: parseInt(`${elementDetails.styles.width}`.replace('px', ''))
            };
            const {groupElement, blockTranslateX, blockTranslateY, width, height} = getGroupTransforms(JSON.parse(JSON.stringify(newStoredElements[elementIndex])));
            newStoredElements[elementIndex] = groupElement;
            newStyles = { ...newStyles,
              translateX: `${blockTranslateX}px`,
              translateY: `${blockTranslateY}px`,
              height:`${height}px`,
              width: `${width}px`
            };
            updateElementStyles(newStyles);
            break;
          case 'column-styles':
            let { styles, headerId } = details;
            const headerElementIndex = storedElements.findIndex(element => element.uuid === headerId);
            newStoredElements[elementIndex].elementDetails.value.tableHeader = newStoredElements[elementIndex].elementDetails.value.tableHeader
              .map(header => {
                if (header.uuid === headerId) {
                  return Object.assign({}, header, {
                    elementDetails: { ...header.elementDetails, ...{ styles: { ...header.elementDetails.styles, ...styles } }}
                  });
                }
                return header;
              });
            elementWidth = newStoredElements[elementIndex].elementDetails.value.tableHeader.reduce((acc, header) =>
              acc + parseInt(`${header.elementDetails.styles.width}`.replace("px", ""), 10), 0);
            newStoredElements[headerElementIndex].elementDetails.styles = {
              ...newStoredElements[headerElementIndex].elementDetails.styles,
              ...styles
            };
            newStoredElements[elementIndex].elementDetails.styles.width = `${elementWidth}px`;
            break;
          case 'table-moveable-styles': {
              const { elementStyles, columnStyles, rowStyles, titleStyles } = details;
              newStoredElements[elementIndex].elementDetails.styles = {
                ...newStoredElements[elementIndex].elementDetails.styles,
                ...elementStyles,
              };
              if (elementStyles.width) {
                  newStoredElements[elementIndex].elementDetails.value.tableHeader = newStoredElements[elementIndex].elementDetails.value.tableHeader
                    .map((header, index) => {
                      return Object.assign({}, header, {
                        elementDetails: {
                          ...header.elementDetails, ...{
                            styles: {
                              ...header.elementDetails.styles,
                              width: columnStyles[index].width,
                              height: columnStyles[index].height
                            }
                          }
                        }
                      });
                    });
                    newStoredElements[elementIndex].elementDetails.value.tableHeader.forEach((header, index) => {
                      const headerElementIndex = storedElements.findIndex(element => element.uuid === header.uuid);
                      newStoredElements[headerElementIndex].elementDetails.styles = {
                        ...newStoredElements[headerElementIndex].elementDetails.styles,
                        width: columnStyles[index].width,
                        height: columnStyles[index].height
                      };
                      newStoredElements[headerElementIndex].elementDetails.value = columnStyles[index].value;
                    });
                    newStoredElements[elementIndex].elementDetails.value.rowStyles = newStoredElements[elementIndex].elementDetails.value.rowStyles.map((styles, styleIndex) => {
                      return Object.assign({}, styles, rowStyles[styleIndex]);
                    })
                    if (titleStyles) {
                        newStoredElements[elementIndex].elementDetails.value.title.elementDetails.styles = {
                          ...newStoredElements[elementIndex].elementDetails.value.title.elementDetails.styles,
                          height: titleStyles.height
                        };
                        newStoredElements[elementIndex].elementDetails.value.title.elementDetails.value = titleStyles.value;
                        const titleElementIndex = storedElements.findIndex(element => element.uuid === newStoredElements[elementIndex].elementDetails.value.title.uuid);
                        newStoredElements[titleElementIndex].elementDetails.styles.height = titleStyles.height;
                        newStoredElements[titleElementIndex].elementDetails.value = titleStyles.value;
                    }
                }
              updateElementStyles({
                width: newStoredElements[elementIndex].elementDetails.styles.width,
                height: newStoredElements[elementIndex].elementDetails.styles.height,
                uuid: `overflow-table_${uuid}`
              });
              break;
          }
          case 'table-header-styles':
            newStoredElements[elementIndex].elementDetails.styles = {
              ...newStoredElements[elementIndex].elementDetails.styles,
              ...details.styles,
            };
            const tableElementIndex = newStoredElements.findIndex(el => el.uuid === details.parentElement);
            const newTableHeight = parseInt(`${newStoredElements[tableElementIndex].elementDetails.styles.height}`.replace('px', ''))
              + parseInt(`${details.styles.height}`.replace('px', ''));
            newStoredElements[tableElementIndex].elementDetails.styles = {
              ...newStoredElements[tableElementIndex].elementDetails.styles,
              height: newTableHeight
            };
            updateElementStyles({
              height: newTableHeight,
              uuid: details.parentElement
            });
            updateElementStyles({
              height: newTableHeight,
              uuid: `overflow-table_${details.parentElement}`
            });
            break;
          case 'table-column':
            let { tableHeader, newHeader, variableName, rowStyles, styles: tableStyles, ...rest } = details;
            if (newHeader) {
              newStoredElements.push(newHeader);
            }
            if (tableHeader) {
              newStoredElements[elementIndex].elementDetails.value.tableHeader = tableHeader;
              newStoredElements[elementIndex].elementDetails.value.tableHeader.forEach((header, index) => {
                const newWidth = `${header.elementDetails.styles.width}`.endsWith('px') ? header.elementDetails.styles.width : `${header.elementDetails.styles.width}px`;
                const newHeight = `${header.elementDetails.styles.height}`.endsWith('px') ? header.elementDetails.styles.height : `${header.elementDetails.styles.height}px`;
                newStoredElements[elementIndex].elementDetails.value.tableHeader[index] = Object.assign({}, header, {
                  elementDetails: { ...header.elementDetails, ...{ styles: { ...header.elementDetails.styles,
                  width: newWidth,
                  height: newHeight
                  }}}
                });
                const headerElementIndex = newStoredElements.findIndex(element => element.uuid === header.uuid);
                newStoredElements[headerElementIndex].elementDetails.styles = {
                  ...newStoredElements[headerElementIndex].elementDetails.styles,
                  width: newWidth,
                  height: newHeight
                };
              });
              elementWidth = tableHeader.reduce((acc, header) =>
                acc + parseInt(`${header.elementDetails.styles.width}`.replace("px", ""), 10), 0);
              newStoredElements[elementIndex].elementDetails.styles.width = `${elementWidth}px`;
              updateElementStyles({ ...newStoredElements[elementIndex].elementDetails.styles, uuid: elementId });
            }
            if (rowStyles) {
              newStoredElements[elementIndex].elementDetails.value.rowStyles = rowStyles;
              updateElementStyles({ ...newStoredElements[elementIndex].elementDetails.styles, uuid: elementId });
            }
            if (variableName) {
              newStoredElements[elementIndex].elementDetails.value.variableName = variableName;
            }
            if (tableStyles) {
              newStoredElements[elementIndex].elementDetails.styles = {
                ...newStoredElements[elementIndex].elementDetails.styles,
                ...tableStyles
              };
              updateElementStyles({ ...newStoredElements[elementIndex].elementDetails.styles, uuid: elementId });
            }
            if (Object.keys(rest).length !== 0) {
              newStoredElements[elementIndex].elementDetails.value = {
                ...newStoredElements[elementIndex].elementDetails.value,
                ...rest
              };
            }
            setTimeout(() => updateElementStyles({ ...newStoredElements[elementIndex].elementDetails.styles, uuid: `overflow-table_${elementId}` }), 100);
            break;
          case 'table-theme':
            newStoredElements[elementIndex].elementDetails.value.rowStyles = details.rowStyles.map(styles => ({
              ...styles,
              backgroundColor: `rgba(${styles.backgroundColor})`,
              borderColor: `rgba(${styles.borderColor})`,
              color: `rgba(${styles.color})`,
            }));
            const headerStyles = {
              background: details.headerStyles.background.length > 3 ? `rgba(${details.headerStyles.background})` : `rgb(${details.headerStyles.background})`,
              borderWidth: details.headerStyles.borderWidth,
              borderColor: details.headerStyles.borderColor?.length > 3 ? `rgba(${details.headerStyles.borderColor})` : `rgb(${details.headerStyles.borderColor})`,
              color: details.headerStyles.color?.length > 3 ? `rgba(${details.headerStyles.color})` : `rgb(${details.headerStyles.color})`,
              alignment: details.headerStyles.alignment,
              cellPadding: details.headerStyles.cellPadding
            }
            newStoredElements[elementIndex].elementDetails.value.headerStyles = headerStyles;
            // let headerElements = [];
            newStoredElements[elementIndex].elementDetails.value.tableHeader.forEach((header, index) => {
              newStoredElements[elementIndex].elementDetails.value.tableHeader[index] = Object.assign({}, header, {
                elementDetails: { ...header.elementDetails, ...{ styles: { ...header.elementDetails.styles, ...headerStyles } }}
              });
              const headerElementIndex = storedElements.findIndex(element => element.uuid === header.uuid);
              // headerElements.push(headerElementIndex);
              newStoredElements[headerElementIndex].elementDetails.styles = {
                ...newStoredElements[headerElementIndex].elementDetails.styles,
                ...headerStyles
              };
            });
            break;
          case 'overflow-table-styles':
            let newTransform;
            const transformValues = getValuesFromTransform(details.transform);
            const existingTransformValues = getValuesFromTransform(newStoredElements[elementIndex].elementDetails.styles.transform);
            newTransform = `translateX(${transformValues.translateX}px) translateY(${existingTransformValues.translateY}px) rotate(0deg) scaleX(1) scaleY(1)`;
            newStoredElements[elementIndex].elementDetails.styles = {
              ...newStoredElements[elementIndex].elementDetails.styles,
              ...{ transform: newTransform },
            };
            newStoredElements[elementIndex].elementDetails.value.overflowStartY = transformValues.translateY;
            updateElementStyles({ translateX: transformValues.translateY, uuid: elementId })
            break;
          default:
            break;
        }
      }
      handlePageHistory([newStoredElements[elementIndex]]);
      return newStoredElements;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateStoredElements = useCallback(elements => {
    setElementsStore(elements);
    handlePageHistory(elements);
  }, [handlePageHistory]);

  const updateTemplateDetails = useCallback((type, value) => {
    switch (type) {
      case 'template':
        setCurrentTemplate(value);
        break;

      default:
        break;
    }
  }, []);

  const handlePageHistory = useCallback(
    (elements, updateType = 'add') => {
      if (isEmail) return;
      switch (updateType) {
        case 'add':
          setUpdatePages(oldPages => {
              let newPages = [...oldPages];
              for (const element of elements) {
                const { parentPageId } = element;
                if (parentPageId && !oldPages.includes(parentPageId)) {
                  newPages = [...newPages, parentPageId];
                }
              }
              return newPages;
          });
          break;
        case 'delete':
          setUpdatePages(updatedPages => updatedPages.filter(data => data !== elements.pageId));
          if (elements.deleteId !== undefined && elements.deleteId !== null) {
            setDeletedPages(deletePages => [...deletePages, elements.deleteId ]);
          }
          break;
        case 'applyAll':
          setApplyAll(true);
          break;
        case 'reset':
          setUpdatePages([]);
          setDeletedPages([]);
          setApplyAll(false);
          break;
        default:
          break;
      }
    },
    [],
  );

  return (
    <PageElementsContext.Provider
      value={{
        storedElements,
        handleStoredElements,
        updateStoredElementDetails,
        updateComplexElementDetails,
        updateStoredElements,
        updatedElementStyles,
        updateElementStyles,
        currentTemplate,
        updateTemplateDetails,
        pagesUpdated,
        handlePageHistory,
        applyAll,
        isDraft,
        setIsDraft,
        deletedPages,
        setSendForTesting,
        sendForTesting,
        isTableEditing,
        setTableEditing,
        isTableFormatEnabled,
        setTableFormat,
        setCheckBoxFocused,
        isCheckBoxFocused: focusedCheckBox,
        focusedCheckBox,
        generatingText,
        setGeneratingText,
        deleteElementDetails,
        swapElements,
        isMovableClick, 
        setMovableClick,
        copyElements,
        pasteElements,
        copyGridElement,
        pasteGridElements,
        deleteCellContnet,
        groupingElements,
        groupedElementIds,
        setGroupedElementIds,
        groupName,
        setGroupName,
      }}
    >
      {children}
    </PageElementsContext.Provider>
  );
};

export default React.memo(PageElementsProvider);
